import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import api, { demandes as demandesApi } from '../services/api';
import referentielsApi from '../services/referentiels';
import concessionsApi from '../services/concessions';
import sujetsApi from '../services/sujets';
import smsApi, { isFixedDomTomNumber } from '../services/sms';
import PhoneLookup from './PhoneLookup';
import ImmatLookup from './ImmatLookup';
import aiApi from '../services/ai';
import axios from 'axios';
import './Dashboard.css'; // Réutilise le CSS existant
import './LeadsServices.css';
import SearchableSelect from './SearchableSelect';
import takataApi from '../services/takata';
import { DetailDossierModal, CreateDossierModal } from './Takata';
import DemandeVoletAtelier from './DemandeVoletAtelier';
import { useDossierPlanning } from './shared/DossierPlanification';

import { texteTheme } from '../utils/theme';
// Masquage RGPD — demandes > 6 mois pour les non-super-admins (données intactes en base)
const RGPD_REDACTED = <span style={{ color: 'var(--text-muted)', letterSpacing: '0.12em' }}>●●●●●●●●●●</span>;
const SIX_MOIS_MS = 6 * 30 * 24 * 60 * 60 * 1000;
const isRgpdMasked = (demande, user) =>
  user?.role !== 'super-admin' && demande.date_demande &&
  Date.now() - new Date(demande.date_demande).getTime() > SIX_MOIS_MS;

const BRAND_LOGOS = {
  'Honda':   'https://upload.wikimedia.org/wikipedia/commons/thumb/3/38/Honda.svg/120px-Honda.svg.png',
  'Suzuki':  'https://upload.wikimedia.org/wikipedia/commons/thumb/3/31/Suzuki_Motor_Corporation_logo.svg/120px-Suzuki_Motor_Corporation_logo.svg.png',
  'Nissan':  'https://upload.wikimedia.org/wikipedia/commons/thumb/2/23/Nissan_2020_logo.svg/120px-Nissan_2020_logo.svg.png',
  'Opel':    'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2b/Opel_logo_2023.svg/120px-Opel_logo_2023.svg.png',
  'Mazda':   'https://upload.wikimedia.org/wikipedia/commons/thumb/4/46/Mazda_logo_2024.svg/120px-Mazda_logo_2024.svg.png',
  'Ford':    'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3e/Ford_logo_flat.svg/120px-Ford_logo_flat.svg.png',
  'Renault': 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/49/Renault_2021_Text.svg/120px-Renault_2021_Text.svg.png',
  'Peugeot': 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f7/Peugeot_Logo.svg/120px-Peugeot_Logo.svg.png',
  'Citroën': 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fd/Citroen_2022_Logo.svg/120px-Citroen_2022_Logo.svg.png',
  'Toyota':  'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9d/Toyota_carlogo.svg/120px-Toyota_carlogo.svg.png',
  'Hyundai': 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/Hyundai_symbol.svg/120px-Hyundai_symbol.svg.png',
  'Skoda':   'https://upload.wikimedia.org/wikipedia/commons/thumb/5/52/%C5%A0koda_Auto.svg/120px-%C5%A0koda_Auto.svg.png',
};

const COMMENTAIRES_SUGGESTIONS = {
  'Takata':            ['Demande de RDV TAKATA', 'Pièce TAKATA commandée, en attente de livraison', 'Rappel campagne TAKATA en cours'],
  'Avancement':        ['Souhaite connaître l\'avancement des travaux', 'Véhicule toujours en attente de pièces', 'Demande de mise à jour sur le dossier'],
  'Rappel':            ['Demande de rappel', 'Client à rappeler dans la journée', 'Rappel suite à message laissé'],
  'Suivi de commande': ['Suivi commande pièce en cours', 'Véhicule toujours en attente de pièces', 'Date de livraison non confirmée'],
  'Demande de devis':  ['Demande d\'information sur devis', 'Devis envoyé, en attente de réponse'],
  'Remorquage':        ['Véhicule en panne, demande de remorquage', 'Suivi suite à remorquage'],
  'Info':              ['Demande d\'information générale', 'Renseignement sur un service'],
  'Autre':             ['Problème récurrent non résolu', 'Réclamation client', 'Demande spéciale'],
};

const SUJET_PICTOS = {
  'Avancement':        '📊',
  'Rappel':            '🔔',
  'Remorquage':        '🪝',
  'Autre':             '🔹',
  'Info':              'ℹ️',
  'Takata':            '⚠️',
  'Suivi de commande': '📦',
  'Demande de devis':  '💰',
};

function BrandLogo({ nom }) {
  const [failed, setFailed] = React.useState(false);
  const url = BRAND_LOGOS[nom];
  if (!url || failed) {
    return <span className="brand-btn-fallback">{nom.charAt(0)}</span>;
  }
  return (
    <img
      src={url}
      alt={nom}
      onError={() => setFailed(true)}
    />
  );
}

const CONCESSION_LOGOS = {
  'BLUE': '/logos/concessions/blue.svg',
  'CENT': '/logos/concessions/centreauto.svg',
  'SOGU': '/logos/concessions/soguava.svg',
};

const CONCESSION_LABELS = {
  'BLUE': 'Blue Auto',
  'CENT': 'Centre Auto',
  'SOGU': 'Soguava',
};

function ConcessionLogo({ code }) {
  const [failed, setFailed] = React.useState(false);
  const url = CONCESSION_LOGOS[code];
  if (!url || failed) {
    return <span className="brand-btn-fallback">{code?.charAt(0)}</span>;
  }
  return (
    <img
      src={url}
      alt={code}
      onError={() => setFailed(true)}
    />
  );
}

function stripAccents(str) {
  return str
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[œŒ]/g, m => m === 'œ' ? 'oe' : 'OE')
    .replace(/[æÆ]/g, m => m === 'æ' ? 'ae' : 'AE')
    .replace(/[ç]/g, 'c').replace(/[Ç]/g, 'C')
    .replace(/[^a-zA-Z0-9 .,;:!?'"()\-\n/€@+]/g, '');
}

function SmsModal({ demande, onClose }) {
  const [message, setMessage] = useState('');
  const [telephone, setTelephone] = useState(demande.telephone || '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [history, setHistory] = useState([]);
  const [reformulating, setReformulating] = useState(false);
  const [lastDraft, setLastDraft] = useState(null); // pour bouton "Restaurer" après reformulation

  useEffect(() => {
    if (demande.id) {
      smsApi.getHistory(demande.id).then(r => setHistory(r.data.history || [])).catch(() => {});
    }
  }, [demande.id]);

  const handleSend = async () => {
    if (!message.trim() || !telephone.trim()) return;
    const confirmed = window.confirm(
      `Envoyer ce SMS ?\n\nDestinataire : ${telephone.trim()}\n\nMessage :\n${message.trim()}`
    );
    if (!confirmed) return;
    setError(''); setSuccess(false); setSending(true);
    try {
      await smsApi.send({
        demande_id: demande.id,
        telephone: telephone,
        message: message.trim(),
      });
      setSuccess(true);
      setMessage('');
      // Recharger l'historique
      const r = await smsApi.getHistory(demande.id);
      setHistory(r.data.history || []);
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de l'envoi du SMS");
    } finally {
      setSending(false);
    }
  };

  const handleReformuler = async () => {
    if (!message.trim() || !demande.id || reformulating) return;
    setError('');
    setReformulating(true);
    const draftAvant = message;
    try {
      const r = await aiApi.reformulerSms(draftAvant.trim(), demande.id);
      const propose = (r.data?.message || '').trim();
      if (!propose) throw new Error('Réponse IA vide');
      setLastDraft(draftAvant);
      setMessage(propose);
    } catch (err) {
      setError(err.response?.data?.error || "Reformulation IA indisponible");
    } finally {
      setReformulating(false);
    }
  };

  const handleRestoreDraft = () => {
    if (lastDraft === null) return;
    setMessage(lastDraft);
    setLastDraft(null);
  };

  const charCount = message.length;
  const charNearLimit = charCount >= 140;
  const phoneBlocked = isFixedDomTomNumber(telephone);

  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📱 Envoyer un SMS</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {phoneBlocked && (
            <div style={{ padding: '10px 14px', borderRadius: '8px', background: 'var(--color-error-light)', color: 'light-dark(var(--color-error), var(--color-error-dark))', border: '1px solid var(--color-error)', fontSize: '13px', marginBottom: '14px' }}>
              ⚠️ Numéro fixe DOM-TOM (préfixe 0590 / 0596 / 0594) — l'envoi de SMS est interdit sur ces lignes.
            </div>
          )}

          {/* Infos client */}
          <div style={{ background: 'var(--bg-muted)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
              {demande.nom} {demande.prenom}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '2px' }}>
              Demande #{demande.id} — {demande.sujet}
            </div>
          </div>

          {/* Numéro de téléphone */}
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '14px' }}>
            Numéro de téléphone
            <input
              type="text"
              value={telephone}
              onChange={e => setTelephone(e.target.value)}
              placeholder="0696XXXXXX"
              style={{ display: 'block', width: '100%', marginTop: '4px', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-default)', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </label>

          {/* Message */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600 }}>Message</label>
            {demande.id && (
              <div style={{ display: 'flex', gap: '6px' }}>
                {lastDraft !== null && (
                  <button
                    type="button"
                    onClick={handleRestoreDraft}
                    disabled={reformulating}
                    title="Revenir à votre brouillon initial"
                    style={{
                      background: 'var(--bg-muted)', color: 'var(--text-secondary)',
                      border: '1px solid var(--border-default)', borderRadius: '6px',
                      padding: '4px 10px', fontSize: '12px', cursor: 'pointer',
                    }}
                  >
                    ↶ Restaurer mon brouillon
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleReformuler}
                  disabled={reformulating || !message.trim()}
                  title={!message.trim() ? 'Saisissez un brouillon à reformuler' : 'Reformuler avec l\'IA en intégrant le contexte de la demande'}
                  style={{
                    background: reformulating ? 'var(--bg-muted)' : 'var(--color-accent, #0891b2)',
                    color: reformulating ? 'var(--text-secondary)' : '#fff',
                    border: 'none', borderRadius: '6px',
                    padding: '4px 10px', fontSize: '12px', fontWeight: 600,
                    cursor: (reformulating || !message.trim()) ? 'not-allowed' : 'pointer',
                    opacity: (reformulating || !message.trim()) ? 0.6 : 1,
                  }}
                >
                  {reformulating ? '… reformulation' : '✨ Reformuler avec l\'IA'}
                </button>
              </div>
            )}
          </div>
          <textarea
            value={message}
            onChange={e => { setMessage(stripAccents(e.target.value).slice(0, 160)); setLastDraft(null); }}
            maxLength={160}
            placeholder="Saisissez votre message…"
            rows={4}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-default)',
              fontSize: '14px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '12px', marginTop: '4px', color: charNearLimit ? '#f59e0b' : '#94a3b8' }}>
            {charCount}/160
          </div>

          {/* Messages d'état */}
          {error && (
            <div style={{ padding: '10px 14px', borderRadius: '8px', background: 'var(--color-error-light)', color: 'light-dark(var(--color-error), var(--color-error-dark))', border: '1px solid var(--color-error)', fontSize: '13px', marginTop: '8px' }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ padding: '10px 14px', borderRadius: '8px', background: 'var(--color-success-light)', color: 'var(--color-success-dark)', border: '1px solid var(--color-success)', fontSize: '13px', marginTop: '8px' }}>
              SMS envoyé avec succès !
            </div>
          )}

          {/* Historique SMS */}
          {history.length > 0 && (
            <div style={{ marginTop: '24px', borderTop: '1px solid var(--border-default)', paddingTop: '16px' }}>
              <h4 style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 700, color: 'var(--text-secondary)' }}>
                Historique SMS ({history.length})
              </h4>
              <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {history.map(h => (
                  <div key={h.id} style={{
                    padding: '10px 14px', borderRadius: '8px', fontSize: '13px',
                    background: h.statut === 'envoye' ? 'light-dark(#f8fafc, var(--bg-muted))' : 'light-dark(#fef2f2, var(--bg-muted))',
                    border: `1px solid ${h.statut === 'envoye' ? 'light-dark(#e2e8f0, var(--border-default))' : 'light-dark(#fecaca, var(--border-strong))'}`,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {h.user_nom} {h.user_prenom}
                      </span>
                      <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                        {new Date(h.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div style={{ color: 'var(--text-secondary)' }}>{h.message}</div>
                    {h.statut === 'erreur' && (
                      <div style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))', fontSize: '12px', marginTop: '4px' }}>Erreur : {h.erreur}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Fermer</button>
          <button
            onClick={handleSend}
            disabled={sending || !message.trim() || !telephone.trim() || phoneBlocked}
            className="btn-primary"
            title={phoneBlocked ? 'SMS interdit : numéro fixe DOM-TOM' : undefined}
            style={{ opacity: (sending || !message.trim() || !telephone.trim() || phoneBlocked) ? 0.5 : 1 }}
          >
            {sending ? 'Envoi…' : '📱 Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LeadsServices({ user, onNavigate, editMode = false, canDelete = false, setHeaderAction }) {
  // Ouvrir un dossier d'atelier SANS quitter Demandes clients : même modale et
  // même hook que les sept écrans qui portent la colonne « Planification ».
  // Le volet atelier proposait déjà le branchement (`onOuvrirDossier`), mais
  // personne ne le remplissait : on basculait donc vers le Planning.
  // `voletVersion` force le volet à se relire après un enregistrement, sinon
  // il afficherait encore le statut d'avant dans la demande restée ouverte.
  const [voletVersion, setVoletVersion] = useState(0);
  const { ouvrir: ouvrirDossierAtelier, modale: modaleDossierAtelier } = useDossierPlanning({
    user,
    editMode,
    onEnregistre: () => setVoletVersion(v => v + 1),
  });
  // États
  const [demandes, setDemandes] = useState([]);
  const [archives, setArchives] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [marques, setMarques] = useState([]);
  const [modeles, setModeles] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const smsActiveConcessions = new Set(concessions.filter(c => c.sms_actif).map(c => c.id));
  const [sujets, setSujets] = useState([]);
  const myResponsableId = responsables.find(r => r.user_id === user.id)?.id;
  const [stats, setStats] = useState({ total: 0, urgents: 0, archives: 0 });
  const [view, setView] = useState('actives');
  const [dashboardArchives, setDashboardArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('all');
  const chipsRowRef = useRef(null);
  const [filterResponsable, setFilterResponsable] = useState(() => {
    if ((user?.dashboard_scope || 'own') === 'own') return 'all';
    return localStorage.getItem('filter_responsable') || 'all';
  });
  const [filterConcession] = useState('all'); // concession : choix du menu (2026-10-26)
  const [filterService, setFilterService] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [prefillData, setPrefillData] = useState(null); // pré-remplissage sans mode édition (Ringover)
  const [editingDemande, setEditingDemande] = useState(null);

  // Bouton d'action dans le header (initialisé ici, mis à jour après filteredDemandes)
  const handleExportPDFRef = useRef(null);
  const [extractingKeywords, setExtractingKeywords] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [takataImmats, setTakataImmats] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const importFileRef = useRef(null);
  const [incomingCall, setIncomingCall] = useState(null); // { from_number, concession_nom, concession_id }
  const [smsModal, setSmsModal] = useState(null); // demande pour laquelle envoyer un SMS
  const [showTakataSearch, setShowTakataSearch] = useState(false);
  const [takataImmatSearch, setTakataImmatSearch] = useState('');
  const [takataSearchResult, setTakataSearchResult] = useState(null); // null = pas cherché, 'not_found', ou dossier
  const [takataSearching, setTakataSearching] = useState(false);
  const [takataSelectedDossier, setTakataSelectedDossier] = useState(null);
  const [showTakataCreate, setShowTakataCreate] = useState(false);

  useEffect(() => {
    if (chipsRowRef.current) chipsRowRef.current.scrollLeft = 0;
  }, []);

  useEffect(() => {
    loadData();
    loadReferentiels();
    loadSujets();
    loadTakataImmats();
    // loadConcessions gère l'opérateur via getMy() — nécessaire pour alimenter
    // smsActiveConcessions et activer le bouton SMS sur la concession de l'opérateur.
    loadConcessions();
  }, []);

  // SSE — écoute les appels Ringover entrants (téléopérateurs + super-admin)
  useEffect(() => {
    if (user.role !== 'teleoperateur' && user.role !== 'super-admin') return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const apiBase = process.env.REACT_APP_API_URL || '';
    const es = new EventSource(`${apiBase}/api/webhooks/ringover/events?token=${token}`);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'incoming_call') {
          // Super-admin ne voit que les numéros avec visible_superadmin activé
          if (user.role === 'super-admin' && !data.visible_superadmin) return;
          setIncomingCall(data);
        }
      } catch (_) {}
    };
    es.onerror = () => { /* reconnexion automatique gérée par le navigateur */ };
    return () => es.close();
  }, []);

  useEffect(() => {
    if (view === 'archives') loadArchives();
  }, [view, searchTerm]);

  useEffect(() => {
    if (view === 'dashboard') loadDashboardArchives();
  }, [view]);

  // Deep-link : si on arrive via "Voir la demande créée" depuis Leads Web,
  // ouvrir directement la demande visée. La clé localStorage est posée par
  // LeadsView.js et consommée ici une seule fois.
  useEffect(() => {
    const targetId = localStorage.getItem('demandes_open_id');
    if (!targetId || demandes.length === 0) return;
    const target = demandes.find(d => String(d.id) === String(targetId));
    if (target) {
      setEditingDemande(target);
      setShowModal(true);
    }
    localStorage.removeItem('demandes_open_id');
  }, [demandes]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [demandesRes, statsRes] = await Promise.all([
        demandesApi.getAll(),
        demandesApi.getStats()
      ]);
      setDemandes(demandesRes.data.demandes);
      setStats(statsRes.data.stats);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      alert('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadReferentiels = async () => {
    try {
      const [respRes, marqRes, modRes] = await Promise.all([
        referentielsApi.getResponsables(),
        referentielsApi.getMarques(),
        referentielsApi.getModeles()
      ]);
      setResponsables(respRes.data.responsables);
      setMarques(marqRes.data.marques);
      setModeles(modRes.data.modeles);
    } catch (error) {
      console.error('Erreur chargement référentiels:', error);
    }
  };

  const loadSujets = async () => {
    try {
      const response = await sujetsApi.getAll();
      setSujets(response.data.sujets);
    } catch (error) {
      console.error('Erreur chargement sujets:', error);
    }
  };

  const loadTakataImmats = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/takata/concerned-immats', { headers: { Authorization: `Bearer ${token}` } });
      setTakataImmats(new Set(data));
    } catch { /* silencieux */ }
  };

  const loadArchives = async () => {
    try {
      const response = await demandesApi.getArchives({ search: searchTerm });
      setArchives(response.data.archives);
    } catch (error) {
      console.error('Erreur chargement archives:', error);
    }
  };

  const loadDashboardArchives = async () => {
    try {
      const response = await demandesApi.getArchives({ search: '' });
      setDashboardArchives(response.data.archives);
    } catch (error) {
      console.error('Erreur chargement archives dashboard:', error);
    }
  };

  const loadConcessions = async () => {
    try {
      if (user.role === 'super-admin' || user.role === 'admin' || user.role === 'teleoperateur') {
        const response = await concessionsApi.getAll();
        setConcessions(response.data.concessions || []);
      } else {
        // Opérateur : charger uniquement sa concession
        const response = await concessionsApi.getMy();
        if (response.data.concession) {
          setConcessions([response.data.concession]);
        }
      }
    } catch (error) {
      console.error('Erreur chargement concessions:', error);
    }
  };

  const handleSaveDemande = async (demandeData) => {
    // ⚠️ La synthèse IA est désormais gérée côté serveur en arrière-plan
    // (POST /api/demandes → setImmediate). Elle ne bloque plus la création.
    // Cf. backend/src/routes/demandes.js + backend/src/services/ai-synthesize.js
    // pour le détail (timeout 3 s, 0 retries, fail-silent).
    try {
      if (editingDemande?.id) {
        await demandesApi.update(editingDemande.id, demandeData);
      } else {
        await demandesApi.create(demandeData);
      }

      loadData();
      setShowModal(false);
      setEditingDemande(null);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleArchive = async (motif) => {
    if (!motif || !motif.trim()) return;
    try {
      const demande = demandes.find(d => d.id === archiveTarget);
      await demandesApi.archive(archiveTarget, motif.trim());
      setArchiveTarget(null);
      loadData();

      if (demande?.created_at) {
        const elapsed = Date.now() - new Date(demande.created_at).getTime();
        if (elapsed < 3600000) {
          confetti({
            particleCount: 60,
            spread: 65,
            origin: { x: 0.5, y: 0.15 },
            colors: ['#0891b2', '#67e8f9', '#e2e8f0', '#ffffff', '#0e7490'],
            ticks: 180,
            scalar: 0.85,
            gravity: 1.3,
          });
          setToast(true);
          setTimeout(() => setToast(false), 3500);
        }
      }
    } catch (error) {
      console.error('Erreur archivage:', error);
      alert('Erreur lors de l\'archivage');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette demande ?\n\nElle part dans la corbeille et reste restaurable pendant 30 jours.')) return;
    try {
      await demandesApi.delete(id);
      loadData();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const filteredDemandes = demandes.filter(d => {
    const s = searchTerm.toLowerCase();
    const matchSearch = !searchTerm ||
      d.nom?.toLowerCase().includes(s) ||
      d.prenom?.toLowerCase().includes(s) ||
      d.telephone?.includes(searchTerm) ||
      d.email?.toLowerCase().includes(s) ||
      d.immatriculation?.toLowerCase().includes(s) ||
      d.marque?.toLowerCase().includes(s) ||
      d.modele?.toLowerCase().includes(s) ||
      d.sujet?.toLowerCase().includes(s) ||
      d.responsable_nom?.toLowerCase().includes(s) ||
      d.responsable_prenom?.toLowerCase().includes(s) ||
      d.responsable?.toLowerCase().includes(s) ||
      d.concession_nom?.toLowerCase().includes(s) ||
      d.commentaire_initial?.toLowerCase().includes(s);

    const matchStatut = filterStatut === 'all'
      || (filterStatut === '__unassigned__' ? !d.responsable_id : d.sujet === filterStatut);
    const matchResponsable = filterResponsable === 'all' || d.responsable_id === parseInt(filterResponsable);
    const effectiveConcessionId = filterConcession !== 'all' ? parseInt(filterConcession) : user?.concession_id || null;
    const matchConcession = !effectiveConcessionId || d.concession_id === effectiveConcessionId;
    const matchService = filterService === 'all' || (d.responsable_service || '') === filterService;

    return matchSearch && matchStatut && matchResponsable && matchConcession && matchService;
  });

  const filteredArchives = archives.filter(a => {
    const s = searchTerm.toLowerCase();
    const matchSearch = !searchTerm ||
      a.nom?.toLowerCase().includes(s) ||
      a.prenom?.toLowerCase().includes(s) ||
      a.telephone?.includes(searchTerm) ||
      a.email?.toLowerCase().includes(s) ||
      a.immatriculation?.toLowerCase().includes(s) ||
      a.marque?.toLowerCase().includes(s) ||
      a.modele?.toLowerCase().includes(s) ||
      a.sujet?.toLowerCase().includes(s) ||
      a.responsable?.toLowerCase().includes(s) ||
      a.commentaire_initial?.toLowerCase().includes(s);

    const effectiveConcessionIdArch = filterConcession !== 'all' ? parseInt(filterConcession) : user?.concession_id || null;
    const matchConcession = !effectiveConcessionIdArch || a.concession_id === effectiveConcessionIdArch;

    return matchSearch && matchConcession;
  });

  // Demandes filtrées par responsable + concession (pour les compteurs par sujet)
  const effectiveConcessionIdCount = filterConcession !== 'all' ? parseInt(filterConcession) : user?.concession_id || null;
  const demandesParResponsable = demandes.filter(d =>
    (filterResponsable === 'all' || d.responsable_id === parseInt(filterResponsable)) &&
    (!effectiveConcessionIdCount || d.concession_id === effectiveConcessionIdCount)
  );

  // Chip « À attribuer » — file d'attribution du call-center (demandes créées par
  // l'agent vocal, sans responsable). Restriction temporaire (phase de test) :
  // le backend ne renvoie ces lignes qu'au super-admin (cf. GET /demandes) —
  // pour les autres rôles ce compteur est toujours 0, donc le chip reste masqué
  // ici aussi le temps que le call-center soit prêt à les recevoir.
  const canSeeUnassigned = user.role === 'super-admin';
  const countUnassigned = demandesParResponsable.filter(d => !d.responsable_id).length;

  // Export PDF du tableau
  handleExportPDFRef.current = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR');
    doc.setFontSize(14);
    doc.text(`Demandes clients — ${dateStr}`, 14, 15);
    doc.setFontSize(9);
    doc.text(`Exporté le ${dateStr} à ${now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`, 14, 21);

    const head = [['Date', 'Client', 'Téléphone', 'Véhicule', 'Immat.', 'Sujet', 'Synthèse', 'Responsable', 'Concession']];
    const body = filteredDemandes.map(d => [
      new Date(d.created_at).toLocaleDateString('fr-FR'),
      [d.nom, d.prenom].filter(Boolean).join(' '),
      d.telephone || '',
      [d.marque, d.modele].filter(Boolean).join(' '),
      d.immatriculation || '',
      d.sujet || '',
      d.synthese || '',
      [d.responsable_nom, d.responsable_prenom].filter(Boolean).join(' '),
      d.concession_nom || ''
    ]);

    autoTable(doc, {
      head, body, startY: 25,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 244, 237] },
    });

    doc.save(`demandes-clients-${now.toISOString().split('T')[0]}.pdf`);
  };

  // Bouton d'action dans le header
  useEffect(() => {
    if (setHeaderAction) {
      setHeaderAction({
        secondary: [
          { label: '🛡 Identification Takata', onClick: () => setShowTakataSearch(true) },
          ...(user.role === 'super-admin' ? [
            { label: (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: 4 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Export PDF
              </>
            ), onClick: () => handleExportPDFRef.current?.() }
          ] : []),
        ],
        primary: { label: '+ Nouvelle demande', onClick: () => { setEditingDemande(null); setShowModal(true); } }
      });
    }
  }, [setHeaderAction, user.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResponsableChange = (value) => {
    setFilterResponsable(value);
    localStorage.setItem('filter_responsable', value);
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const XLSX = await import('xlsx');
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const raw = XLSX.utils.sheet_to_json(ws, { header: 1 });
    if (raw.length < 2) return;
    const COL_MAP = {
      'date': 'date', 'Date': 'date', 'DATE': 'date',
      'concession': 'concession', 'Concession': 'concession', 'CONCESSION': 'concession',
      'nom': 'nom', 'Nom': 'nom', 'NOM': 'nom',
      'prenom': 'prenom', 'prénom': 'prenom', 'Prénom': 'prenom', 'PRENOM': 'prenom', 'PRÉNOM': 'prenom',
      'telephone': 'telephone', 'téléphone': 'telephone', 'Téléphone': 'telephone', 'TELEPHONE': 'telephone', 'TÉLÉPHONE': 'telephone',
      'responsable': 'responsable', 'Responsable': 'responsable', 'RESPONSABLE': 'responsable',
      'marque': 'marque', 'Marque': 'marque', 'MARQUE': 'marque',
      'immatriculation': 'immatriculation', 'Immatriculation': 'immatriculation', 'IMMATRICULATION': 'immatriculation',
      'message': 'message', 'Message': 'message', 'MESSAGE': 'message',
    };
    const headers = raw[0].map(h => String(h || '').trim());
    const rows = raw.slice(1).map(row => {
      const obj = {};
      headers.forEach((h, i) => {
        const key = COL_MAP[h];
        if (key) obj[key] = String(row[i] ?? '').trim();
      });
      return obj;
    }).filter(r => r.nom && r.telephone);
    setImportRows(rows);
    setImportResult(null);
  };

  const handleImport = async () => {
    setImportLoading(true);
    try {
      const res = await demandesApi.importDemandes(importRows);
      setImportResult(res.data);
      loadData();
    } catch (err) {
      alert('Erreur lors de l\'import');
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div className="page-container">
      {/* Onglets */}
      <div className="page-tabs">
        <button className={view === 'actives' ? 'active' : ''} onClick={() => setView('actives')}>
          📋 Demandes actives <span className="tab-count">{filteredDemandes.length}</span>
        </button>
        <button className={view === 'archives' ? 'active' : ''} onClick={() => setView('archives')}>
          🗄️ Archives
        </button>
        <button className={view === 'dashboard' ? 'active' : ''} onClick={() => setView('dashboard')}>
          📊 Tableau de bord
        </button>
        {canDelete && (
          <button className={view === 'corbeille' ? 'active' : ''} onClick={() => setView('corbeille')}>
            🗑️ Corbeille
          </button>
        )}
        {(user.role === 'super-admin' || user.is_manager) && (
          <button className={view === 'suivi' ? 'active' : ''} onClick={() => setView('suivi')}>
            👥 Suivi collaborateurs
          </button>
        )}
        {user.role === 'super-admin' && (
          <button className={view === 'call-center' ? 'active' : ''} onClick={() => setView('call-center')}>
            🎙️ Call-center IA<span className="tab-sa-lock" title="Visible par les super-admins uniquement">🔒</span>
          </button>
        )}
      </div>

      {/* Toolbar : recherche + filtres uniquement */}
      {view !== 'call-center' && <div className="page-toolbar">
        <input
          type="text"
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {view === 'actives' && (user.dashboard_scope || 'own') !== 'own' && (() => {
          const scope = user.dashboard_scope || 'own';
          const respList = scope === 'all'
            ? responsables
            : responsables.filter(r => demandes.some(d => d.responsable_id === r.id));
          return (
          <SearchableSelect
            value={filterResponsable}
            options={[
              { value: 'all', label: 'Tous les responsables' },
              ...respList.map(r => ({ value: String(r.id), label: `${r.nom} ${r.prenom}`.trim() }))
            ]}
            onChange={handleResponsableChange}
            placeholder="Tous les responsables"
            highlighted={filterResponsable !== 'all'}
          />
          ); })()}

        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}

        {(() => {
          const servicesUniques = [...new Set(demandes.map(d => d.responsable_service).filter(Boolean))].sort();
          if (servicesUniques.length < 2) return null;
          return (
            <select
              className={`page-select${filterService !== 'all' ? ' active-filter' : ''}`}
              value={filterService}
              onChange={(e) => setFilterService(e.target.value)}
            >
              <option value="all">Tous les services</option>
              {servicesUniques.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          );
        })()}
      </div>}

      {/* Filter chips */}
      {view !== 'call-center' && <div className="filter-chips-bar">
        <span className="filter-chips-label">Filtrer :</span>
        <div className="filter-chips-row" ref={chipsRowRef}>
          <button
            className={`filter-chip ${filterStatut === 'all' ? 'chip-active' : 'chip-default'}`}
            onClick={() => { setView('actives'); setFilterStatut('all'); }}
          >
            Toutes <span className="chip-count">{demandesParResponsable.length}</span>
          </button>
          {canSeeUnassigned && (
            <button
              className={`filter-chip ${filterStatut === '__unassigned__' ? 'chip-active' : 'chip-default'} ${countUnassigned === 0 ? 'chip-empty' : ''}`}
              onClick={() => { if (countUnassigned > 0) { setView('actives'); setFilterStatut('__unassigned__'); } }}
              disabled={countUnassigned === 0}
              title="Demandes créées par l'agent vocal, en attente d'attribution"
            >
              🎙️ À attribuer <span className="chip-count">{countUnassigned}</span>
            </button>
          )}
          {sujets.map(s => {
            const count = demandesParResponsable.filter(d => d.sujet === s.nom).length;
            const isActive = filterStatut === s.nom;
            return (
              <button
                key={s.id}
                className={`filter-chip ${isActive ? 'chip-active' : 'chip-default'} ${count === 0 ? 'chip-empty' : ''}`}
                onClick={() => { if (count > 0) { setView('actives'); setFilterStatut(s.nom); } }}
                disabled={count === 0}
              >
                {s.nom} <span className="chip-count">{count}</span>
              </button>
            );
          })}
        </div>
      </div>}

      {/* Contenu */}
      {loading && <div className="loading">Chargement...</div>}

      {!loading && view === 'actives' && (
        <DemandesView
          demandes={filteredDemandes}
          user={user}
          filterResponsable={filterResponsable}
          onEdit={(demande) => {
            setEditingDemande(demande);
            setShowModal(true);
          }}
          onArchive={(id) => setArchiveTarget(id)}
          onDelete={handleDelete}
          onSms={setSmsModal}
          smsActiveConcessions={smsActiveConcessions}
          editMode={editMode}
          canDelete={canDelete}
          takataImmats={takataImmats}
        />
      )}

      {!loading && view === 'mes-demandes' && (
        <DemandesView
          demandes={filteredDemandes.filter(d => d.responsable_id === myResponsableId)}
          user={user}
          filterResponsable={filterResponsable}
          onEdit={(demande) => {
            setEditingDemande(demande);
            setShowModal(true);
          }}
          onArchive={(id) => setArchiveTarget(id)}
          onDelete={handleDelete}
          onSms={setSmsModal}
          smsActiveConcessions={smsActiveConcessions}
          editMode={editMode}
          canDelete={canDelete}
          takataImmats={takataImmats}
        />
      )}

      {!loading && view === 'archives' && (
        <ArchivesView
          archives={filteredArchives}
          user={user}
          onUnarchived={() => { loadArchives(); loadData(); }}
        />
      )}

      {view === 'dashboard' && (
        <DashboardLeadsView demandes={demandes} archives={dashboardArchives} />
      )}

      {view === 'corbeille' && canDelete && (
        <CorbeilleView onRestauree={() => loadData()} />
      )}

      {view === 'suivi' && (user.role === 'super-admin' || user.is_manager) && (
        <SuiviCollaborateursView concessionId={user?.concession_id || (filterConcession !== 'all' ? filterConcession : null)} />
      )}

      {view === 'call-center' && user.role === 'super-admin' && (
        <CallCenterView user={user} />
      )}

      {/* Bannière appel entrant Ringover */}
      {incomingCall && !showModal && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
          background: '#1e293b', color: 'var(--text-inverse)', borderRadius: '12px',
          padding: '16px 20px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '300px',
          animation: 'slideIn 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700', fontSize: '16px' }}>
            <span style={{ fontSize: '20px' }}>📞</span>
            {incomingCall.client_nom
              ? `${incomingCall.client_prenom || ''} ${incomingCall.client_nom}`.trim()
              : 'Client inconnu'}
          </div>
          {incomingCall.concession_nom && (
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              {incomingCall.concession_nom}
            </div>
          )}
          {incomingCall.demandes_actives > 0 && (
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'light-dark(var(--color-warning), var(--color-warning-dark))', background: 'rgba(245,158,11,0.15)', padding: '4px 10px', borderRadius: '6px', marginTop: '2px' }}>
              ⚠️ {incomingCall.demandes_actives} demande{incomingCall.demandes_actives > 1 ? 's' : ''} en cours
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
            <button
              style={{ flex: 1, background: '#3b82f6', color: 'var(--text-inverse)', border: 'none', borderRadius: '8px', padding: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}
              onClick={() => {
                setEditingDemande(null);
                setPrefillData({
                  telephone: incomingCall.from_number,
                  concession_id: incomingCall.concession_id || '',
                  source_contact: 'appel',
                  nom: incomingCall.client_nom || '',
                  prenom: incomingCall.client_prenom || '',
                });
                setShowModal(true);
                setIncomingCall(null);
              }}
            >
              {incomingCall.demandes_actives > 0 ? 'Nouvelle demande' : 'Créer une demande'}
            </button>
            <button
              style={{ background: '#334155', color: 'var(--text-muted)', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', fontSize: '13px' }}
              onClick={() => setIncomingCall(null)}
            >
              Ignorer
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <DemandeModal
          key={editingDemande?.id || 'new'}
          demande={editingDemande}
          prefill={prefillData}
          responsables={responsables}
          marques={marques}
          modeles={modeles}
          concessions={concessions}
          sujets={sujets}
          user={user}
          onSave={handleSaveDemande}
          onOpenExisting={(d) => setEditingDemande(d)}
          onOuvrirDossier={(intervention) => ouvrirDossierAtelier({ planning_intervention_id: intervention.id })}
          voletVersion={voletVersion}
          onArchiver={(d) => { setShowModal(false); setEditingDemande(null); setArchiveTarget(d.id); }}
          onClose={() => {
            setShowModal(false);
            setEditingDemande(null);
            setPrefillData(null);
          }}
        />
      )}

      {/* Dossier d'atelier ouvert par-dessus la demande (rendu après elle) */}
      {modaleDossierAtelier}

      {/* Toast félicitations */}
      {toast && (
        <div className="bravo-toast">
          <span className="bravo-toast-icon">⚡</span>
          <div>
            <div className="bravo-toast-title">Traité en moins d'1 heure !</div>
            <div className="bravo-toast-sub">Bravo !</div>
          </div>
        </div>
      )}

      {/* Justification d'archivage — modale partagée avec le panneau latéral du
          Planning Atelier (même geste, mêmes motifs types). */}
      {archiveTarget && (
        <ArchiveDemandeModal
          onClose={() => setArchiveTarget(null)}
          onConfirm={(motif) => handleArchive(motif)}
        />
      )}

      {/* ── Modale Import Excel ── */}
      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📥 Import Excel — Demandes clients</h2>
              <button className="modal-close" onClick={() => setShowImportModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
                Le fichier doit contenir les colonnes : <strong>Date, Concession, Nom, Prénom, Téléphone, Responsable, Marque, Immatriculation, Message</strong>.<br />
                Le champ <em>Message</em> sera injecté en demande initiale.
              </p>
              <div
                style={{ border: '2px dashed var(--border-default)', borderRadius: '8px', padding: '24px', textAlign: 'center', cursor: 'pointer', background: 'var(--bg-muted)' }}
                onClick={() => importFileRef.current?.click()}
              >
                <input ref={importFileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImportFile} />
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📄</div>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: '600' }}>Cliquer pour sélectionner un fichier</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>.xlsx ou .xls</div>
              </div>
              {importRows.length > 0 && !importResult && (
                <div style={{ background: 'var(--color-success-light)', border: '1px solid var(--color-success)', borderRadius: '8px', padding: '12px', fontSize: '13px', color: 'var(--color-success-dark)' }}>
                  ✅ <strong>{importRows.length} ligne{importRows.length > 1 ? 's' : ''} détectée{importRows.length > 1 ? 's' : ''}</strong> et prête{importRows.length > 1 ? 's' : ''} à importer.
                </div>
              )}
              {importResult && (
                <div style={{ background: 'var(--bg-muted)', border: '1px solid var(--border-default)', borderRadius: '8px', padding: '12px', fontSize: '13px' }}>
                  <div style={{ fontWeight: '700', marginBottom: '6px' }}>Résultats de l'import :</div>
                  <div style={{ color: 'var(--color-success-dark)' }}>✅ {importResult.created} créée{importResult.created !== 1 ? 's' : ''}</div>
                  {importResult.skipped > 0 && <div style={{ color: 'var(--color-warning-dark)' }}>⚠️ {importResult.skipped} ignorée{importResult.skipped !== 1 ? 's' : ''}</div>}
                  {importResult.errors?.length > 0 && (
                    <ul style={{ marginTop: '8px', paddingLeft: '16px', color: 'var(--color-error-dark)', fontSize: '12px' }}>
                      {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowImportModal(false)}>Fermer</button>
              <button className="btn-primary" onClick={handleImport} disabled={importRows.length === 0 || importLoading || !!importResult}>
                {importLoading ? 'Import en cours…' : `Importer${importRows.length > 0 ? ` (${importRows.length})` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {smsModal && (
        <SmsModal
          demande={smsModal}
          onClose={() => setSmsModal(null)}
        />
      )}

      {/* Modale recherche Takata par immatriculation */}
      {showTakataSearch && !takataSelectedDossier && !showTakataCreate && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🛡 Identification Takata</h2>
              <button className="modal-close" onClick={() => { setShowTakataSearch(false); setTakataImmatSearch(''); setTakataSearchResult(null); }}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: 20 }}>
              <p style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                Saisissez l'immatriculation ou le VIN du véhicule pour vérifier s'il est concerné par un rappel Takata.
              </p>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!takataImmatSearch.trim()) return;
                setTakataSearching(true);
                setTakataSearchResult(null);
                try {
                  const res = await takataApi.searchByImmat(takataImmatSearch.trim());
                  setTakataSearchResult(res.data.dossier || 'not_found');
                } catch { setTakataSearchResult('not_found'); }
                finally { setTakataSearching(false); }
              }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="page-search"
                    style={{ flex: 1 }}
                    placeholder="Ex : AB123CD ou VIN"
                    value={takataImmatSearch}
                    onChange={e => { setTakataImmatSearch(e.target.value.toUpperCase()); setTakataSearchResult(null); }}
                    autoFocus
                  />
                  <button type="submit" className="btn-primary" disabled={takataSearching || !takataImmatSearch.trim()}>
                    {takataSearching ? '…' : 'Rechercher'}
                  </button>
                </div>
              </form>
              {takataSearchResult === 'not_found' && (
                <div style={{ marginTop: 16, padding: 12, background: 'var(--color-success-light)', borderRadius: 8, color: 'light-dark(var(--color-success), var(--color-success-dark))', fontWeight: 600 }}>
                  ✅ Aucun dossier Takata trouvé pour cette immatriculation.
                  <div style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => {
                        if (window.confirm("Êtes-vous sûr de vouloir créer manuellement un dossier TAKATA ?\n\nAvez-vous vérifié qu'il n'y a aucune faute de frappe lors de la vérification ?")) {
                          setShowTakataCreate(true);
                        }
                      }}
                    >
                      ➕ Créer un dossier Takata — véhicule importé
                    </button>
                  </div>
                </div>
              )}
              {takataSearchResult && takataSearchResult !== 'not_found' && (
                <div style={{ marginTop: 16, padding: 12, background: 'var(--color-error-light)', borderRadius: 8 }}>
                  <div style={{ fontWeight: 600, color: 'light-dark(var(--color-error), var(--color-error-dark))', marginBottom: 8 }}>
                    ⚠️ Véhicule concerné par un rappel Takata
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)', lineHeight: 1.6 }}>
                    <div><strong>VIN :</strong> {takataSearchResult.vin}</div>
                    <div><strong>Marque :</strong> {takataSearchResult.marque} {takataSearchResult.modele}</div>
                    <div><strong>Client :</strong> {[takataSearchResult.client_nom, takataSearchResult.client_prenom].filter(Boolean).join(' ') || '—'}</div>
                  </div>
                  {/* Dossier d'une concession hors de votre périmètre (2026-10-24) :
                      le serveur confirme le rappel sans ouvrir la fiche. */}
                  {takataSearchResult.hors_perimetre ? (
                    <p style={{ marginTop: 12, marginBottom: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      Dossier suivi par {takataSearchResult.concession_nom || 'une autre concession'} : orientez le client vers elle.
                    </p>
                  ) : (
                    <button
                      className="btn-primary"
                      style={{ marginTop: 12, width: '100%' }}
                      onClick={() => { setTakataSelectedDossier(takataSearchResult); }}
                    >
                      Ouvrir le dossier Takata
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modale création dossier Takata (immat pré-remplie depuis la recherche) */}
      {showTakataCreate && !takataSelectedDossier && (
        <CreateDossierModal
          concessions={concessions}
          initialImmat={takataImmatSearch.trim()}
          initialVin={takataImmatSearch.trim()}
          onClose={() => setShowTakataCreate(false)}
          onCreated={(dossier) => {
            // Transition fermeture → ouverture : on démonte la création, puis on
            // ouvre la fiche du nouveau dossier après un court délai (fade modalFadeIn).
            setShowTakataCreate(false);
            setTakataSearchResult(null);
            setTimeout(() => setTakataSelectedDossier(dossier), 200);
          }}
        />
      )}

      {/* Modale dossier Takata complète */}
      {takataSelectedDossier && (
        <DetailDossierModal
          dossier={takataSelectedDossier}
          user={user}
          isAdmin={user?.role === 'super-admin' || user?.role === 'admin'}
          concessions={concessions}
          users={responsables}
          restrictedMode
          onClose={() => { setTakataSelectedDossier(null); setShowTakataSearch(false); setTakataImmatSearch(''); setTakataSearchResult(null); }}
          onGeler={async () => {
            try {
              await takataApi.gelerDossier(takataSelectedDossier.id);
              const res = await takataApi.getDossier(takataSelectedDossier.id);
              setTakataSelectedDossier(res.data.dossier);
            } catch (err) { alert(err.response?.data?.error || 'Erreur'); }
          }}
          onDossierUpdated={(d) => setTakataSelectedDossier(d)}
          onDeleted={() => { setTakataSelectedDossier(null); setShowTakataSearch(false); }}
        />
      )}

    </div>
  );
}

// Composant DemandeModal
// ─── Modale de justification d'archivage ──────────────────────────────────────
// Extraite du corps de LeadsServices (2026-08-14) pour être appelée aussi
// depuis le panneau latéral du Planning Atelier : un seul geste d'archivage
// dans tout le CRM, avec les mêmes motifs types. Le composant ne connaît que
// le motif — l'appel API et le rafraîchissement restent chez l'appelant.
export function ArchiveDemandeModal({ onConfirm, onClose, busy = false }) {
  const [motif, setMotif] = useState('');
  const MOTIFS = [
    'Client contacté par téléphone ce jour',
    'Mail envoyé au client',
    'SMS envoyé au client',
    'Client reçu en concession',
    'Sans réponse après relances',
    'Rendez-vous pris',
    'Demande annulée par le client',
  ];
  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>✅ Archiver la demande</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ marginBottom: '12px', color: 'var(--text-secondary)', fontSize: '14px' }}>
            Veuillez justifier l'archivage de cette demande :
          </p>
          <textarea
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            placeholder="Ex : Client rappelé, tout est ok"
            rows="3"
            autoFocus
            style={{
              width: '100%',
              padding: '10px',
              border: '2px solid var(--border-default)',
              borderRadius: '6px',
              fontSize: '14px',
              marginBottom: '10px'
            }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '4px' }}>
            {MOTIFS.map(texte => (
              <button
                key={texte}
                type="button"
                onClick={() => setMotif(texte)}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  background: motif === texte ? '#3b82f6' : 'light-dark(#f1f5f9, var(--bg-muted))',
                  color: motif === texte ? 'white' : 'light-dark(#475569, var(--text-primary))',
                  border: '1px solid',
                  borderColor: motif === texte ? '#3b82f6' : 'light-dark(#e2e8f0, var(--border-default))',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                {texte}
              </button>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button
            className="btn-primary"
            onClick={() => onConfirm(motif.trim())}
            disabled={busy || !motif.trim()}
          >
            {busy ? 'Archivage…' : 'Archiver'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DemandeModal({ demande, prefill, responsables, marques, modeles, concessions, sujets, user, onSave, onClose, onOpenExisting, onOuvrirDossier = null, voletVersion = 0, onArchiver = null }) {
  const [formData, setFormData] = useState(demande || {
    date_demande: new Date().toISOString().split('T')[0],
    nom: prefill?.nom || '',
    prenom: prefill?.prenom || '',
    telephone: prefill?.telephone || '',
    email: '',
    marque: '',
    modele: '',
    immatriculation: '',
    vin: '',
    sujet: '',
    commentaires: '',
    nb_appels: 1,
    source_contact: prefill?.source_contact || 'appel',
    responsable_id: '',
    bon_de_commande: '',
    concession_id: prefill?.concession_id || ''
  });

  const [smsOuvert, setSmsOuvert] = useState(false);
  const [commentaires, setCommentaires] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [takataAlert, setTakataAlert] = useState(null);
  const [saving, setSaving] = useState(false);

  // Vérifier si l'immat ou le VIN est concerné par la campagne Takata
  useEffect(() => {
    const immat = formData.immatriculation;
    if (!immat || immat.length < 4) { setTakataAlert(null); return; }
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`/api/takata/check-immat?immat=${encodeURIComponent(immat)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTakataAlert(data.concerned ? data.dossier : null);
      } catch { setTakataAlert(null); }
    }, 400);
    return () => clearTimeout(timer);
  }, [formData.immatriculation]);

  useEffect(() => {
    const vin = formData.vin;
    if (!vin || vin.length < 5) { if (!formData.immatriculation) setTakataAlert(null); return; }
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`/api/takata/check-vin?vin=${encodeURIComponent(vin)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (data.concerned) setTakataAlert(data.dossier);
        else if (!formData.immatriculation || formData.immatriculation.length < 4) setTakataAlert(null);
      } catch {}
    }, 400);
    return () => clearTimeout(timer);
  }, [formData.vin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user.role !== 'operateur' && !demande && concessions.length > 0) {
      const lastConcession = localStorage.getItem('last_concession_id');
      if (lastConcession) {
        setFormData(prev => ({ ...prev, concession_id: parseInt(lastConcession) }));
      }
    }
  }, [user.role, demande, concessions]);

  useEffect(() => {
    if (demande?.id) {
      loadCommentaires();
    }
  }, [demande?.id]);

  const loadCommentaires = async () => {
    try {
      setLoadingComments(true);
      const response = await demandesApi.getCommentaires(demande.id);
      setCommentaires(response.data.commentaires);
    } catch (error) {
      console.error('Erreur chargement commentaires:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleUpdateComment = async (commentaire) => {
    if (!editingCommentText.trim()) return;
    try {
      await demandesApi.updateCommentaire(demande.id, commentaire.id, editingCommentText);

      // Si c'est le commentaire initial, régénérer la synthèse IA
      if (commentaire.is_initial && editingCommentText.length > 10) {
        try {
          const { data } = await aiApi.synthesize(editingCommentText);
          if (data.synthese) {
            await demandesApi.update(demande.id, { synthese: data.synthese });
          }
        } catch (err) {
          console.error('Erreur régénération synthèse:', err);
        }
      }

      setEditingCommentId(null);
      setEditingCommentText('');
      loadCommentaires();
    } catch (error) {
      console.error('Erreur modification commentaire:', error);
      alert('Erreur lors de la modification du commentaire');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await demandesApi.addCommentaire(demande.id, newComment);
      setNewComment('');
      loadCommentaires();
    } catch (error) {
      console.error('Erreur ajout commentaire:', error);
      alert('Erreur lors de l\'ajout du commentaire');
    }
  };

  const handleConcessionChange = async (concessionId) => {
    // Reset le responsable si il n'appartient pas à la nouvelle concession
    setFormData(prev => {
      const currentResp = responsables.find(r => r.id === parseInt(prev.responsable_id));
      const respStillValid = currentResp && currentResp.concession_id === parseInt(concessionId);
      return {
        ...prev,
        concession_id: concessionId,
        responsable_id: respStillValid ? prev.responsable_id : ''
      };
    });
    if (user.role !== 'operateur') {
      localStorage.setItem('last_concession_id', concessionId);
    }
    if (demande) {
      try {
        await demandesApi.update(demande.id, { concession_id: concessionId });
      } catch (error) {
        console.error('Erreur sauvegarde concession:', error);
        alert('Erreur lors de la sauvegarde de la concession');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;                               // garde-fou anti double-submit
    if (!formData.responsable_id) {
      alert('Veuillez sélectionner un responsable.');
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  // Volet Atelier actif dès qu'un véhicule est identifiable : la modale
  // s'élargit (1200px) et le formulaire garde sa colonne d'origine.
  const voletAtelierActif = String(formData.immatriculation || '').replace(/[^a-zA-Z0-9]/g, '').length >= 4
    || String(formData.vin || '').replace(/[^a-zA-Z0-9]/g, '').length >= 8;

  return (
    <div className="modal-overlay">
      <div className={`modal-content leads-modal-content${voletAtelierActif ? ' large' : ''}`}>
        {/* ── Header : titre + concession + fermer ──────────────────── */}
        <div className="modal-header">
          <h2>{demande ? 'Modifier la demande' : 'Nouvelle demande'}</h2>
          {user.role !== 'operateur' && (
            <div className={`concession-picker${!formData.concession_id ? ' concession-picker--required' : ''}`}>
              {concessions.map(c => (
                <button
                  key={c.id}
                  type="button"
                  className={`concession-btn${formData.concession_id === c.id ? ' selected' : ''}`}
                  onClick={() => handleConcessionChange(c.id)}
                  title={c.nom}
                >
                  {CONCESSION_LABELS[c.code] || c.nom.split(' ')[0]}
                </button>
              ))}
            </div>
          )}
          <button type="button" onClick={onClose} className="modal-close">✕</button>
        </div>

        <div className={`leads-body-row${voletAtelierActif ? '' : ' leads-body-row--seul'}`}>
        <form onSubmit={handleSubmit} className="leads-modal-form">
          <div className="form-scroll-body">

          {/* ── Date + Source + Nb appels ─────────────────────────────── */}
          <div className="modal-meta-bar">
            <div className="form-group">
              <label>📅 Date</label>
              <input
                type="date"
                value={formData.date_demande}
                onChange={demande ? undefined : (e) => setFormData(prev => ({ ...prev, date_demande: e.target.value }))}
                readOnly={!!demande}
                required={!demande}
                style={demande ? { background: 'var(--bg-muted)', color: 'var(--text-secondary)', cursor: 'default' } : {}}
              />
            </div>
            <div className="form-group" style={{ textAlign: 'center' }}>
              <label>Source du contact</label>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px', justifyContent: 'center' }}>
                {[
                  { value: 'appel',      label: '📞 Appel' },
                  { value: 'mail',       label: '✉️ Email' },
                  { value: 'concession', label: '🏢 Concession' },
                ].map(src => (
                  <button
                    key={src.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, source_contact: src.value }))}
                    style={{
                      padding: '5px 12px', borderRadius: '8px', border: '2px solid',
                      borderColor: formData.source_contact === src.value ? '#0891b2' : 'light-dark(#e2e8f0, var(--border-default))',
                      background: formData.source_contact === src.value ? 'light-dark(#e0f2fe, var(--color-info-light))' : 'light-dark(white, var(--bg-surface))',
                      color: formData.source_contact === src.value ? 'light-dark(#0891b2, var(--color-accent-dark))' : 'light-dark(#475569, var(--text-primary))',
                      fontWeight: formData.source_contact === src.value ? '700' : '500',
                      cursor: 'pointer', fontSize: '12px',
                    }}
                  >
                    {src.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Nb appels</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                <button type="button"
                  onClick={() => setFormData(prev => ({ ...prev, nb_appels: Math.max(0, (prev.nb_appels || 0) - 1) }))}
                  style={{ width: '26px', height: '26px', borderRadius: '50%', border: '2px solid var(--border-default)', background: 'var(--bg-muted)', cursor: 'pointer', fontWeight: '700', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                >−</button>
                <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: (formData.nb_appels || 0) >= 3 ? '#ef4444' : (formData.nb_appels || 0) === 2 ? '#f97316' : '#0891b2', color: 'var(--text-inverse)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '15px', flexShrink: 0 }}>
                  {formData.nb_appels || 0}
                </div>
                <button type="button"
                  onClick={() => setFormData(prev => ({ ...prev, nb_appels: (prev.nb_appels || 0) + 1 }))}
                  style={{ width: '26px', height: '26px', borderRadius: '50%', border: '2px solid #0891b2', background: 'light-dark(#e0f2fe, var(--color-info-light))', cursor: 'pointer', fontWeight: '700', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'light-dark(var(--color-accent), var(--color-accent-dark))' }}
                >+</button>
              </div>
            </div>
          </div>

          {/* ── Section CLIENT ────────────────────────────────────────── */}
          <div className="form-section">
            <div className="form-section-hd">👤 Client</div>
            <div className="form-grid">
              <div className="form-group">
                <label className="required">Nom</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value.toUpperCase() }))}
                  required
                  placeholder=""
                />
              </div>
              <div className="form-group">
                <label>Prénom</label>
                <input
                  type="text"
                  value={formData.prenom}
                  onChange={(e) => {
                    const value = e.target.value;
                    const formatted = value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '';
                    setFormData(prev => ({ ...prev, prenom: formatted }));
                  }}
                  placeholder=""
                />
              </div>
              <div className="form-group">
                <label className="required">Téléphone</label>
                <input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^\d+\s\-().]/g, '');
                    setFormData(prev => ({ ...prev, telephone: cleaned }));
                  }}
                  placeholder="0612345678 ou +33 6 12 34 56 78"
                  required
                />
                {formData.telephone && formData.telephone.replace(/\D/g, '').length >= 7 && (
                  <small style={{ color: 'light-dark(var(--color-success), var(--color-success-dark))', fontWeight: '600' }}>✓ Numéro valide</small>
                )}
                {formData.telephone && formData.telephone.replace(/\D/g, '').length > 0 && formData.telephone.replace(/\D/g, '').length < 7 && (
                  <small style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))', fontWeight: '600' }}>
                    ✗ Numéro trop court
                  </small>
                )}
              </div>
              <div className="form-group">
                <label>Email</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="client@example.com"
                    style={{ flex: 1 }}
                  />
                  {formData.email && (
                    <span style={{ fontSize: '18px', flexShrink: 0 }} title={/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? 'Email valide' : 'Format invalide'}>
                      {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? '✅' : '❌'}
                    </span>
                  )}
                </div>
                <div className="email-domain-btns">
                  {['@gmail.com', '@outlook.fr', '@hotmail.fr'].map(domain => (
                    <button
                      key={domain}
                      type="button"
                      className="email-domain-btn"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, email: (prev.email || '').split('@')[0] + domain }));
                      }}
                    >
                      {domain}
                    </button>
                  ))}
                </div>
              </div>
              {!demande && formData.telephone && formData.telephone.length >= 10 && (
                <div className="form-group full">
                  <PhoneLookup
                    telephone={formData.telephone}
                    onOpenDemande={(d) => onOpenExisting(d)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* ── Section VÉHICULE ──────────────────────────────────────── */}
          <div className="form-section">
            <div className="form-section-hd">🚗 Véhicule</div>
            <div className="form-grid">
              <div className="form-group full">
                <label>Marque</label>
                <div className="brand-picker">
                  {marques.map(m => (
                    <button
                      key={m.id}
                      type="button"
                      className={`brand-btn${formData.marque === m.nom ? ' selected' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, marque: m.nom, modele: '' }))}
                    >
                      <BrandLogo nom={m.nom} />
                      {m.nom}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-row-3">
                <div className="form-group">
                  <label>Modèle</label>
                  <input
                    type="text"
                    list="modeles-list"
                    value={formData.modele}
                    onChange={(e) => setFormData(prev => ({ ...prev, modele: e.target.value }))}
                    placeholder="Sélectionnez d'abord une marque"
                    disabled={!formData.marque}
                  />
                  <datalist id="modeles-list">
                    {modeles
                      .filter(m => m.marque_nom === formData.marque)
                      .map(m => (
                        <option key={m.id} value={m.nom} />
                      ))}
                  </datalist>
                </div>
                <div className="form-group">
                  <label>Immatriculation</label>
                  <input
                    type="text"
                    value={formData.immatriculation}
                    onChange={(e) => {
                      const cleaned = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                      setFormData(prev => ({ ...prev, immatriculation: cleaned }));
                    }}
                    placeholder="AA123BB"
                  />
                </div>
                {!demande && formData.immatriculation && formData.immatriculation.length >= 4 && (
                  <div className="form-group full">
                    <ImmatLookup
                      immatriculation={formData.immatriculation}
                      onOpenDemande={(d) => onOpenExisting(d)}
                    />
                  </div>
                )}
                {takataAlert && (
                  <div className="takata-immat-alert">
                    ⚠️ <strong>Concerné par la campagne TAKATA</strong> — VIN {takataAlert.vin} ({[takataAlert.marque, takataAlert.modele].filter(Boolean).join(' ')})
                  </div>
                )}
                <div className="form-group">
                  <label>VIN</label>
                  <input
                    type="text"
                    value={formData.vin || ''}
                    onChange={(e) => {
                      const cleaned = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 17);
                      setFormData(prev => ({ ...prev, vin: cleaned }));
                    }}
                    placeholder="VF1RFD00…"
                    maxLength={17}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── Section DEMANDE ───────────────────────────────────────── */}
          <div className="form-section">
            <div className="form-section-hd">📋 Demande</div>
            <div className="form-grid">
              <div className="form-group full">
                <label className="required">Sujet</label>
                <div className="sujet-picker">
                  {sujets.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      className={`sujet-btn${formData.sujet === s.nom ? ' selected' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, sujet: s.nom }))}
                    >
                      <span className="sujet-btn-picto">{SUJET_PICTOS[s.nom] || '🔷'}</span>
                      {s.nom}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', gridColumn: '1 / -1', position: 'relative', zIndex: 2 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="required">Responsable</label>
                  <SearchableSelect
                    value={String(formData.responsable_id || '')}
                    onChange={(val) => setFormData(prev => ({ ...prev, responsable_id: val ? parseInt(val) : '' }))}
                    options={responsables
                      .filter(r => !formData.concession_id || r.concession_id === parseInt(formData.concession_id))
                      .map(r => {
                        const nom = `${r.nom} ${r.prenom || ''}`.trim();
                        return {
                          value: String(r.id),
                          label: r.fonction ? `${nom} — ${r.fonction}` : nom,
                        };
                      })}
                    placeholder="Rechercher un responsable..."
                  />
                </div>
                <div className="form-group" style={{ width: '180px', flexShrink: 0 }}>
                  <label>N° Bon de commande</label>
                  <input
                    type="text"
                    value={formData.bon_de_commande || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, bon_de_commande: e.target.value }))}
                    placeholder=""
                  />
                </div>
              </div>
              {!demande && (
                <div className="form-group full">
                  <label>Commentaire initial</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                    {(formData.sujet
                      ? (COMMENTAIRES_SUGGESTIONS[formData.sujet] || [])
                      : Object.values(COMMENTAIRES_SUGGESTIONS).flat()
                    ).map(txt => (
                      <button
                        key={txt}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, commentaire_initial: txt }))}
                        style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border-strong)', background: 'var(--bg-muted)', color: 'var(--text-primary)', fontSize: '12px', cursor: 'pointer', fontWeight: '500' }}
                      >
                        {txt}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={formData.commentaire_initial || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, commentaire_initial: e.target.value }))}
                    rows="3"
                    placeholder="Décrivez la demande initiale du client..."
                  />
                </div>
              )}
              {demande?.id && (
                <div className="form-group full">
                  <label>💬 Historique des commentaires</label>
                  {loadingComments ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement...</div>
                  ) : commentaires.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontStyle: 'italic' }}>Aucun commentaire de suivi</div>
                  ) : (
                    <div className="comment-history-wrap" style={{ maxHeight: '300px', overflowY: 'auto', padding: '10px', background: 'var(--bg-muted)', borderRadius: '8px', marginBottom: '15px' }}>
                      {commentaires.map(c => (
                        <div key={c.id} className="comment-item" style={{ background: c.is_initial ? 'light-dark(#f0fdf4, var(--bg-muted))' : 'light-dark(white, var(--bg-surface))', padding: '12px', marginBottom: '10px', borderRadius: '6px', borderLeft: `3px solid ${c.is_initial ? '#16a34a' : '#0891b2'}` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', fontSize: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <strong style={{ color: c.is_initial ? 'light-dark(#16a34a, var(--color-success-dark))' : 'light-dark(#0891b2, var(--color-accent-dark))' }}>{c.user_nom} {c.user_prenom}</strong>
                              {c.is_initial && (
                                <span style={{ fontSize: '10px', background: 'var(--color-success-light)', color: 'var(--color-success-dark)', padding: '1px 6px', borderRadius: '10px', fontWeight: '600' }}>Demande initiale</span>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ color: 'var(--text-secondary)' }}>
                                {new Date(c.created_at).toLocaleDateString('fr-FR')} à{' '}
                                {new Date(c.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                {c.updated_at && <em style={{ color: 'var(--text-muted)', marginLeft: '4px' }}>(modifié)</em>}
                              </span>
                              {c.user_id === user.id && editingCommentId !== c.id && (
                                <button type="button" onClick={() => { setEditingCommentId(c.id); setEditingCommentText(c.commentaire); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', padding: '0 2px', color: 'var(--text-secondary)' }} title="Modifier ce commentaire">✏️</button>
                              )}
                            </div>
                          </div>
                          {editingCommentId === c.id ? (
                            <div>
                              <textarea value={editingCommentText} onChange={(e) => setEditingCommentText(e.target.value)} rows="3" autoFocus style={{ width: '100%', padding: '8px', border: '2px solid #0891b2', borderRadius: '6px', fontSize: '14px', marginBottom: '8px', boxSizing: 'border-box' }} />
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button type="button" className="btn-primary" onClick={() => handleUpdateComment(c)} disabled={!editingCommentText.trim()} style={{ width: 'auto', fontSize: '13px', padding: '5px 12px' }}>Enregistrer</button>
                                <button type="button" className="btn-secondary" onClick={() => { setEditingCommentId(null); setEditingCommentText(''); }} style={{ width: 'auto', fontSize: '13px', padding: '5px 12px' }}>Annuler</button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ color: 'var(--text-primary)', fontSize: '14px', whiteSpace: 'pre-wrap' }}>{c.commentaire}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div>
                    <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Ajouter un commentaire de suivi..." rows="3" style={{ width: '100%', padding: '10px', border: '2px solid var(--border-default)', borderRadius: '6px', marginBottom: '10px', fontSize: '14px' }} />
                    <button type="button" className="btn-primary" onClick={handleAddComment} disabled={!newComment.trim()} style={{ width: 'auto' }}>✅ Ajouter commentaire</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          </div>{/* /form-scroll-body */}
          <div className="modal-footer">
            {/* Répondre au client sans quitter la demande — le même geste dans la
                page Demandes clients ET dans le Planning Atelier (la modale est
                partagée). Le bouton reste VISIBLE mais désactivé quand l'envoi est
                impossible : le réceptionnaire doit savoir POURQUOI (concession sans
                SMS, numéro fixe DOM-TOM, numéro manquant). */}
            {demande && (() => {
              const concessionSms = concessions.find(c => c.id === formData.concession_id)?.sms_actif === true;
              const tel = (formData.telephone || '').trim();
              const telFixe = isFixedDomTomNumber(tel);
              const bloque = !concessionSms || !tel || telFixe;
              const pourquoi = !concessionSms ? "SMS non activé pour cette concession"
                : !tel ? "Renseignez le téléphone du client"
                : telFixe ? "SMS interdit : numéro fixe DOM-TOM (0590 / 0596 / 0594)"
                : "Envoyer un SMS à ce client";
              return (
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ marginRight: 'auto', opacity: bloque ? 0.5 : 1 }}
                  disabled={bloque}
                  title={pourquoi}
                  onClick={() => setSmsOuvert(true)}
                >
                  📱 SMS
                </button>
              );
            })()}
            {!demande && (() => {
              const checks = [
                { label: 'Nom',        ok: !!formData.nom?.trim() },
                { label: 'Téléphone', ok: (formData.telephone || '').length === 10 },
                { label: 'Sujet',      ok: !!formData.sujet },
                { label: 'Responsable',ok: !!formData.responsable_id },
              ];
              const done = checks.filter(c => c.ok).length;
              const total = checks.length;
              const allDone = done === total;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: allDone ? 'light-dark(#10b981, var(--color-success-dark))' : 'light-dark(#64748b, var(--text-secondary))' }}>
                  <span style={{ fontWeight: '700', color: allDone ? 'light-dark(#10b981, var(--color-success-dark))' : '#f59e0b' }}>{done}/{total}</span>
                  {checks.map(c => (
                    <span key={c.label} title={c.label} style={{ fontSize: '14px' }}>{c.ok ? '✅' : '⬜'}</span>
                  ))}
                </div>
              );
            })()}
            <div style={{ display: 'flex', gap: '10px' }}>
              {/* Archiver depuis la demande ouverte : le geste ne doit pas
                  obliger à refermer pour retrouver la ligne dans la liste.
                  Rendu seulement si l'appelant sait l'exécuter. */}
              {demande && onArchiver && (
                <button type="button" className="btn-secondary" onClick={() => onArchiver(demande)} disabled={saving}>
                  ✅ Archiver
                </button>
              )}
              <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? '⏳ Enregistrement…' : (demande ? 'Modifier' : 'Créer')}
              </button>
            </div>
          </div>
        </form>
        {voletAtelierActif && (
          <DemandeVoletAtelier
            immatriculation={formData.immatriculation}
            vin={formData.vin}
            onOuvrirDossier={onOuvrirDossier}
            version={voletVersion}
          />
        )}
        </div>
      </div>
      {/* Le numéro et l'identité viennent du FORMULAIRE en cours, pas de la copie
          enregistrée : un numéro corrigé à l'instant est celui qu'on veut joindre. */}
      {smsOuvert && (
        <SmsModal
          demande={{
            ...demande,
            telephone: formData.telephone,
            nom: formData.nom,
            prenom: formData.prenom,
            sujet: formData.sujet,
          }}
          onClose={() => setSmsOuvert(false)}
        />
      )}
    </div>
  );
}

// Composant DemandesView
function DemandesView({ demandes, user, filterResponsable, onEdit, onArchive, onDelete, onSms, smsActiveConcessions = new Set(), editMode = false, canDelete = false, takataImmats = new Set() }) {
  const [sortCol, setSortCol] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [phonePopup, setPhonePopup] = useState(null); // { id, nom, prenom, telephone, x, y }

  const handlePhoneClick = (e, d) => {
    e.stopPropagation();
    if (phonePopup && phonePopup.id === d.id) {
      setPhonePopup(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setPhonePopup({ id: d.id, nom: d.nom, prenom: d.prenom, telephone: d.telephone, x: rect.left, y: rect.bottom + 6 });
    }
  };

  const showResponsable = filterResponsable === 'all';

  // Largeurs colonnes (px) — redimensionnables
  const COL_DEFAULTS = { created_at: 62, concession_nom: 110, client: 150, marque: 100, sujet: 220, responsable: 120, actions: 110 };
  const [colWidths, setColWidths] = useState(COL_DEFAULTS);

  const activeColumns = [
    'created_at',
    ...(user.role !== 'operateur' && user.role !== 'super-admin' ? ['concession_nom'] : []),
    'client', 'marque', 'sujet',
    ...(showResponsable ? ['responsable'] : []),
    'actions',
  ];

  const handleResizeStart = (colKey) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidths = { ...colWidths };
    const idx = activeColumns.indexOf(colKey);
    const nextKey = activeColumns[idx + 1];

    const onMove = (e) => {
      const delta = e.clientX - startX;
      setColWidths(prev => {
        const w = { ...prev };
        if (nextKey) {
          const cur = Math.max(40, startWidths[colKey] + delta);
          const actualDelta = cur - startWidths[colKey];
          w[colKey] = cur;
          w[nextKey] = Math.max(40, startWidths[nextKey] - actualDelta);
        } else {
          w[colKey] = Math.max(40, startWidths[colKey] + delta);
        }
        return w;
      });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const ResizeHandle = ({ colKey }) => (
    <div
      onMouseDown={handleResizeStart(colKey)}
      onClick={e => e.stopPropagation()}
      style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '5px', cursor: 'col-resize', zIndex: 2 }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(100,116,139,0.35)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    />
  );

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const sorted = [...demandes].sort((a, b) => {
    let valA = a[sortCol];
    let valB = b[sortCol];

    if (sortCol === 'created_at') {
      valA = new Date(valA || 0).getTime();
      valB = new Date(valB || 0).getTime();
    } else if (sortCol === 'nb_appels') {
      valA = valA || 0;
      valB = valB || 0;
    } else if (sortCol === 'client') {
      valA = `${a.nom} ${a.prenom}`.toLowerCase();
      valB = `${b.nom} ${b.prenom}`.toLowerCase();
    } else {
      valA = (valA || '').toString().toLowerCase();
      valB = (valB || '').toString().toLowerCase();
    }

    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const colCount = 5
    + (user.role !== 'operateur' ? 1 : 0)
    + (showResponsable ? 1 : 0);

  const SortTh = ({ col, colKey, children }) => (
    <th onClick={() => handleSort(col)} style={{ cursor: 'pointer', userSelect: 'none', position: 'relative', overflow: 'hidden' }}>
      {children} {sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : ''}
      <ResizeHandle colKey={colKey || col} />
    </th>
  );

  // Couleurs cycliques pour les badges Sujet
  const SUJET_COLORS = [
    { bg: 'light-dark(#dbeafe, var(--color-info-light))', text: 'light-dark(#1d4ed8, var(--color-info-dark))' },
    { bg: 'light-dark(#dcfce7, var(--color-success-light))', text: 'light-dark(#16a34a, var(--color-success-dark))' },
    { bg: 'light-dark(#fef3c7, var(--color-warning-light))', text: 'light-dark(#92400e, var(--color-warning-dark))' },
    { bg: 'light-dark(#fce7f3, var(--color-decision-light))', text: 'light-dark(#9d174d, var(--color-decision))' },
    { bg: 'light-dark(#ede9fe, var(--color-decision-light))', text: 'light-dark(#7c3aed, var(--color-decision))' },
    { bg: 'light-dark(#ffedd5, var(--color-paused-light))', text: 'light-dark(#c2410c, var(--color-warning-dark))' },
    { bg: 'light-dark(#cffafe, var(--color-accent-light))', text: 'light-dark(#0e7490, var(--color-accent-dark))' },
  ];
  const getSujetStyle = (s) => {
    if (!s) return { bg: 'light-dark(#f1f5f9, var(--bg-muted))', text: 'light-dark(#475569, var(--text-primary))' };
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h + s.charCodeAt(i)) % SUJET_COLORS.length;
    return SUJET_COLORS[h];
  };

  // Point coloré appels
  const getAppelsBar = (n) => {
    if (n >= 3) return { bar: '#ef4444' };
    if (n === 2) return { bar: '#f97316' };
    return { bar: '#22c55e' };
  };

  return (
    <div className="table-container" onClick={() => setPhonePopup(null)}>
      <style>{`.demandes-table td { overflow: hidden; max-width: 0; }`}</style>
      <table className="demandes-table" style={{ tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          {activeColumns.map(key => <col key={key} style={{ width: colWidths[key] + 'px' }} />)}
        </colgroup>
        <thead>
          <tr>
            <SortTh col="created_at">Date</SortTh>
            {user.role !== 'operateur' && user.role !== 'super-admin' && <SortTh col="concession_nom">Concession</SortTh>}
            <SortTh col="client">Client</SortTh>
            <SortTh col="marque">Véhicule</SortTh>
            <SortTh col="sujet" colKey="sujet">Sujet</SortTh>
            {showResponsable && <SortTh col="responsable">Responsable</SortTh>}
            <th style={{ position: 'relative', overflow: 'hidden' }}>Actions<ResizeHandle colKey="actions" /></th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={colCount} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                Aucune demande active
              </td>
            </tr>
          ) : (
            sorted.map(d => {
              const n = d.nb_appels || 0;
              const c = getAppelsBar(n);
              return (
              <tr key={d.id} className={d.nb_appels >= 3 ? 'urgent' : ''} style={{ cursor: 'pointer' }} onClick={() => onEdit(d)}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {new Date(d.created_at).toLocaleDateString('fr-FR')}
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {new Date(d.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </td>
                {user.role !== 'operateur' && user.role !== 'super-admin' && (
                  <td>
                    <span style={{
                      fontSize: '11px',
                      background: 'var(--bg-muted)',
                      color: 'var(--text-secondary)',
                      padding: '3px 8px',
                      borderRadius: '4px',
                      fontWeight: '600'
                    }}>
                      {d.concession_nom || '-'}
                    </span>
                  </td>
                )}
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <span style={{
                      flexShrink: 0,
                      width: '11px', height: '11px',
                      borderRadius: '50%',
                      background: c.bar,
                      display: 'inline-block',
                    }} title={`${n} appel${n > 1 ? 's' : ''}`} />
                    <div>
                      <div className="font-bold">{isRgpdMasked(d, user) ? RGPD_REDACTED : d.nom}</div>
                      {!isRgpdMasked(d, user) && d.prenom && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>{d.prenom}</div>}
                      {isRgpdMasked(d, user) && <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>🔒 Anonymisé (RGPD)</div>}
                    </div>
                  </div>
                </td>
                <td>
                  {d.marque
                    ? <><span>{d.marque}{d.modele ? ` ${d.modele}` : ''}</span>
                        {d.immatriculation && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px', fontFamily: 'monospace' }}>{d.immatriculation}</div>}
                      </>
                    : <span style={{ color: '#cbd5e1' }}>—</span>}
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '6px' }}>
                    <div>
                      {(() => { const s = getSujetStyle(d.sujet); return (
                        <span style={{ fontSize: '12px', fontWeight: '600', background: s.bg, color: s.text, padding: '3px 8px', borderRadius: '4px', whiteSpace: 'nowrap' }}>
                          {d.sujet || '—'}
                        </span>
                      ); })()}
                      {d.synthese && (
                        <div style={{ marginTop: '5px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                          {d.synthese}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      {d.source === 'agent-vocal' && (
                        <span title="Créée par l'agent vocal IA (call-center)" style={{ background: 'var(--color-accent)', color: 'var(--text-inverse)', fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>🎙️ IA</span>
                      )}
                      {d.immatriculation && takataImmats.has(d.immatriculation.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()) && (
                        <span title="Concerné par la campagne TAKATA" style={{ background: '#dc2626', color: 'var(--text-inverse)', fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>🎯 TAKATA</span>
                      )}
                    </div>
                  </div>
                </td>
                {showResponsable && (
                  <td>
                    {d.responsable_nom
                      ? <><div className="font-bold">{d.responsable_nom}</div>
                          {d.responsable_prenom && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>{d.responsable_prenom}</div>}
                          {d.responsable_service && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px', fontStyle: 'italic' }}>{d.responsable_service}</div>}
                        </>
                      : d.responsable || <span style={{ color: '#cbd5e1' }}>—</span>}
                  </td>
                )}
                <td onClick={e => e.stopPropagation()}>
                  <div className="action-buttons">
                    <button
                      onClick={(e) => !isRgpdMasked(d, user) && handlePhoneClick(e, d)}
                      className="btn-icon"
                      title={isRgpdMasked(d, user) ? 'Données anonymisées (RGPD)' : 'Afficher le numéro'}
                      style={{ visibility: (d.telephone || isRgpdMasked(d, user)) ? 'visible' : 'hidden', opacity: isRgpdMasked(d, user) ? 0.3 : 1, cursor: isRgpdMasked(d, user) ? 'default' : 'pointer' }}
                    >
                      📞
                    </button>
                    <a
                      href={(!isRgpdMasked(d, user) && d.email) ? `mailto:${d.email}?subject=${encodeURIComponent('Votre demande auprès de nos services')}` : undefined}
                      className="btn-icon"
                      title={isRgpdMasked(d, user) ? 'Données anonymisées (RGPD)' : (d.email ? 'Envoyer un mail' : '')}
                      style={{ textDecoration: 'none', visibility: (d.email || isRgpdMasked(d, user)) ? 'visible' : 'hidden', opacity: isRgpdMasked(d, user) ? 0.3 : 1 }}
                    >
                      ✉️
                    </a>
                    {(() => {
                      const concessionOk = smsActiveConcessions.has(d.concession_id);
                      const phoneBlocked = isFixedDomTomNumber(d.telephone);
                      const disabled = isRgpdMasked(d, user) || !concessionOk || phoneBlocked;
                      const title = phoneBlocked
                        ? 'SMS interdit : numéro fixe DOM-TOM (0590 / 0596 / 0594)'
                        : (concessionOk ? 'Envoyer un SMS' : 'SMS non activé pour cette concession');
                      return (
                        <button
                          onClick={() => onSms(d)}
                          className="btn-icon"
                          title={title}
                          disabled={disabled}
                          style={{
                            visibility: d.telephone ? 'visible' : 'hidden',
                            opacity: disabled ? 0.3 : 1,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                          }}
                        >
                          📱
                        </button>
                      );
                    })()}
                    <button onClick={() => onArchive(d.id)} className="btn-icon" title="Archiver">
                      ✅
                    </button>
                    {editMode && canDelete && (
                      <button
                        onClick={() => onDelete(d.id)}
                        className="btn-icon delete"
                        title="Supprimer"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })
          )}
        </tbody>
      </table>

      {phonePopup && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'fixed', left: phonePopup.x, top: phonePopup.y,
            background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '10px',
            padding: '14px 18px', boxShadow: '0 6px 20px rgba(0,0,0,0.13)',
            zIndex: 1000, minWidth: '190px',
          }}
        >
          <button
            onClick={() => setPhonePopup(null)}
            style={{ position: 'absolute', top: '6px', right: '8px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '14px', lineHeight: 1 }}
          >✕</button>
          <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '8px', color: 'var(--text-primary)' }}>
            {phonePopup.nom} {phonePopup.prenom}
          </div>
          <a
            href={`tel:${phonePopup.telephone}`}
            style={{ fontSize: '17px', fontWeight: '600', color: 'var(--color-info-dark)', letterSpacing: '1px', textDecoration: 'none', display: 'block' }}
          >
            📞 {phonePopup.telephone?.replace(/(\d{2})(?=\d)/g, '$1 ')}
          </a>
        </div>
      )}
    </div>
  );
}

// Composant DashboardLeadsView
function DashboardLeadsView({ demandes, archives }) {
  // Combine actives + archives, on utilise created_at et sujet
  const allData = [
    ...demandes.map(d => ({ date: d.created_at, sujet: d.sujet || 'Sans sujet' })),
    ...archives.map(a => ({ date: a.created_at, sujet: a.sujet || 'Sans sujet' })),
  ];

  // Colonnes : sujets uniques triés
  const sujets = [...new Set(allData.map(d => d.sujet))].sort();

  // Lignes : jours uniques (YYYY-MM-DD) triés du plus récent
  const daySet = new Set();
  for (const { date } of allData) {
    if (date) daySet.add(date.slice(0, 10));
  }
  const days = [...daySet].sort().reverse();

  // Matrice [jour][sujet] = count
  const matrix = {};
  for (const { date, sujet } of allData) {
    if (!date) continue;
    const day = date.slice(0, 10);
    if (!matrix[day]) matrix[day] = {};
    matrix[day][sujet] = (matrix[day][sujet] || 0) + 1;
  }

  // Max par cellule pour la teinte
  const maxVal = Math.max(1, ...days.flatMap(day => sujets.map(s => matrix[day]?.[s] || 0)));

  const cellStyle = (count) => {
    if (!count) return { textAlign: 'center', color: '#d1d5db' };
    const intensity = Math.round(20 + (count / maxVal) * 80);
    return {
      textAlign: 'center',
      fontWeight: 600,
      background: `rgba(5, 150, 105, ${intensity / 100 * 0.25})`,
      color: '#065f46',
    };
  };

  const formatDay = (day) => {
    const d = new Date(day + 'T12:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  if (allData.length === 0) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Aucune donnée disponible</div>;
  }

  return (
    <div className="table-container" style={{ overflowX: 'auto' }}>
      <table style={{ minWidth: '600px' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>Jour</th>
            {sujets.map(s => <th key={s} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{s}</th>)}
            <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {days.map(day => {
            const rowTotal = sujets.reduce((sum, s) => sum + (matrix[day]?.[s] || 0), 0);
            return (
              <tr key={day}>
                <td style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{formatDay(day)}</td>
                {sujets.map(s => {
                  const count = matrix[day]?.[s] || 0;
                  return <td key={s} style={cellStyle(count)}>{count || '—'}</td>;
                })}
                <td style={{ textAlign: 'center', fontWeight: 700 }}>{rowTotal}</td>
              </tr>
            );
          })}
          {/* Ligne totaux */}
          <tr style={{ borderTop: '2px solid var(--border-default)', background: 'light-dark(#f9fafb, var(--bg-muted))' }}>
            <td style={{ fontWeight: 700 }}>Total</td>
            {sujets.map(s => {
              const colTotal = days.reduce((sum, day) => sum + (matrix[day]?.[s] || 0), 0);
              return <td key={s} style={{ textAlign: 'center', fontWeight: 700 }}>{colTotal}</td>;
            })}
            <td style={{ textAlign: 'center', fontWeight: 700 }}>
              {days.reduce((sum, day) => sum + sujets.reduce((s2, s) => s2 + (matrix[day]?.[s] || 0), 0), 0)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// Corbeille : demandes supprimées depuis moins de 30 jours, restaurables à
// l'identique (même numéro, commentaires, liens SMS / lead web). Le serveur
// purge définitivement au-delà de 30 jours.
function CorbeilleView({ onRestauree }) {
  const [items, setItems] = useState(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(null);
  const charger = useCallback(async () => {
    try { setErreur(''); setItems((await demandesApi.getCorbeille()).data.corbeille || []); }
    catch (e) { setErreur(e.response?.data?.error || 'Impossible de charger la corbeille'); setItems([]); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const restaurer = async (k) => {
    if (!window.confirm(`Restaurer la demande n° ${k.demande_id} (${[k.nom, k.prenom].filter(Boolean).join(' ') || 'sans nom'}) ?`)) return;
    setEnCours(k.id);
    try {
      await demandesApi.restaurer(k.id);
      await charger();
      if (onRestauree) onRestauree();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la restauration');
    } finally {
      setEnCours(null);
    }
  };

  const dt = (v) => (v ? new Date(v).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
  return (
    <div className="table-container">
      <p style={{ margin: '0 0 12px', color: 'var(--text-secondary)', fontSize: 13 }}>
        Les demandes supprimées restent ici 30 jours, puis sont effacées définitivement. « Restaurer » les remet à l'identique, avec leur numéro et leurs commentaires.
      </p>
      {erreur && <div className="status-error" style={{ marginBottom: 12 }}>{erreur}</div>}
      <table>
        <thead>
          <tr>
            <th>Supprimée le</th>
            <th>N°</th>
            <th>Client</th>
            <th>Sujet</th>
            <th>Concession</th>
            <th>Par</th>
            <th>Purge le</th>
            <th style={{ width: 110, textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items === null ? (
            <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Chargement…</td></tr>
          ) : items.length === 0 ? (
            <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>La corbeille est vide</td></tr>
          ) : items.map(k => (
            <tr key={k.id}>
              <td style={{ whiteSpace: 'nowrap' }}>{dt(k.supprime_le)}</td>
              <td>{k.demande_id}</td>
              <td>
                <div className="font-bold">{k.nom} {k.prenom}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{k.telephone}</div>
              </td>
              <td>{k.sujet || '—'}{k.nb_commentaires > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{k.nb_commentaires} commentaire{k.nb_commentaires > 1 ? 's' : ''}</div>}</td>
              <td>{k.concession_nom || '—'}</td>
              <td>{k.supprime_par_nom || '—'}</td>
              <td style={{ whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{new Date(k.purge_le).toLocaleDateString('fr-FR')}</td>
              <td style={{ textAlign: 'center' }}>
                <button className="btn-secondary" disabled={enCours === k.id} onClick={() => restaurer(k)}>
                  {enCours === k.id ? '…' : '↩️ Restaurer'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Composant ArchivesView
function ArchivesView({ archives, onUnarchived }) {
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [sortCol, setSortCol] = useState('date_archivage');
  const [sortDir, setSortDir] = useState('desc');
  const [unarchivingId, setUnarchivingId] = useState(null);
  // Désarchivage ouvert à tous les utilisateurs ayant accès à la page.
  const canUnarchive = true;

  // Désarchive une demande à partir de son id (utilisé par le bouton de la modale ET de la ligne).
  // Si l'archive est ouverte dans la modale, on ferme la modale après succès.
  const handleUnarchive = async (archiveId) => {
    if (!archiveId) return;
    if (!window.confirm('Désarchiver cette demande ? Elle réapparaîtra dans la liste des demandes actives.')) return;
    setUnarchivingId(archiveId);
    try {
      await demandesApi.unarchive(archiveId);
      if (selectedArchive?.id === archiveId) setSelectedArchive(null);
      if (onUnarchived) onUnarchived();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors du désarchivage');
    } finally {
      setUnarchivingId(null);
    }
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const sorted = [...archives].sort((a, b) => {
    let valA = a[sortCol];
    let valB = b[sortCol];

    if (sortCol === 'date_archivage' || sortCol === 'created_at') {
      valA = new Date(valA || 0).getTime();
      valB = new Date(valB || 0).getTime();
    } else if (sortCol === 'nb_appels' || sortCol === 'duree') {
      if (sortCol === 'duree') {
        valA = new Date(a.date_archivage) - new Date(a.created_at);
        valB = new Date(b.date_archivage) - new Date(b.created_at);
      } else {
        valA = valA || 0;
        valB = valB || 0;
      }
    } else if (sortCol === 'client') {
      valA = `${a.nom} ${a.prenom}`.toLowerCase();
      valB = `${b.nom} ${b.prenom}`.toLowerCase();
    } else {
      valA = (valA || '').toString().toLowerCase();
      valB = (valB || '').toString().toLowerCase();
    }

    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortTh = ({ col, children }) => (
    <th onClick={() => handleSort(col)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {children} {sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            <SortTh col="date_archivage">Date</SortTh>
            <SortTh col="client">Client</SortTh>
            <SortTh col="marque">Véhicule</SortTh>
            <SortTh col="sujet">Sujet</SortTh>
            <SortTh col="responsable">Responsable</SortTh>
            <SortTh col="duree">Durée</SortTh>
            <SortTh col="nb_appels">Appels</SortTh>
            {canUnarchive && <th style={{ width: '60px', textAlign: 'center' }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={canUnarchive ? 8 : 7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                Aucune archive
              </td>
            </tr>
          ) : (
            sorted.map(a => {
              const created = new Date(a.created_at);
              const archived = new Date(a.date_archivage);
              const ms = archived - created;
              const days = Math.floor(ms / (1000 * 60 * 60 * 24));
              const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

              return (
                <tr
                  key={a.id}
                  onClick={() => setSelectedArchive(a)}
                  className={a.nb_appels >= 3 ? 'urgent' : ''}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {archived.toLocaleDateString('fr-FR')}
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      {archived.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </td>
                  <td>
                    <div className="font-bold">{a.nom} {a.prenom}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>{a.telephone}</div>
                    {a.email && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{a.email}</div>}
                  </td>
                  <td>{a.marque} {a.modele}</td>
                  <td>{a.sujet}</td>
                  <td>{a.responsable}</td>
                  <td>
                    <span style={{
                      fontSize: '11px',
                      background: days > 7 ? 'light-dark(#fee2e2, var(--color-error-light))' : 'light-dark(#dcfce7, var(--color-success-light))',
                      color: days > 7 ? 'light-dark(#991b1b, var(--color-error-dark))' : 'light-dark(#166534, var(--color-success-dark))',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontWeight: '600'
                    }}>
                      {days}j {hours}h
                    </span>
                  </td>
                  <td className="text-center">
                    <span className={`badge ${a.nb_appels >= 3 ? 'urgent' : ''}`}>
                      {a.nb_appels}
                    </span>
                  </td>
                  {canUnarchive && (
                    <td className="text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="action-buttons" style={{ justifyContent: 'center' }}>
                        <button
                          className="btn-icon"
                          title="Désarchiver — restaurer dans la liste active"
                          disabled={unarchivingId === a.id}
                          onClick={() => handleUnarchive(a.id)}
                        >
                          {unarchivingId === a.id ? '⏳' : '🔄'}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* Modal détail archive */}
      {selectedArchive && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Archive #{selectedArchive.id}</h2>
              <button className="modal-close" onClick={() => setSelectedArchive(null)}>✕</button>
            </div>

            <div className="modal-body">
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '15px', color: 'var(--text-primary)', borderBottom: '1px solid var(--border-default)', paddingBottom: '8px', marginBottom: '10px' }}>
                  Client
                </h3>
                <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Nom :</strong> {selectedArchive.nom} {selectedArchive.prenom}</p>
                <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Téléphone :</strong>{' '}
                  <a href={`tel:${selectedArchive.telephone}`} style={{ color: 'var(--color-info-dark)', textDecoration: 'none', fontWeight: '600' }}>
                    {selectedArchive.telephone?.replace(/(\d{2})(?=\d)/g, '$1 ')}
                  </a>
                </p>
                {selectedArchive.email && <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Email :</strong> {selectedArchive.email}</p>}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '15px', color: 'var(--text-primary)', borderBottom: '1px solid var(--border-default)', paddingBottom: '8px', marginBottom: '10px' }}>
                  Demande
                </h3>
                <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Sujet :</strong> {selectedArchive.sujet}</p>
                <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Véhicule :</strong> {selectedArchive.marque} {selectedArchive.modele}</p>
                {selectedArchive.immatriculation && <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Immatriculation :</strong> {selectedArchive.immatriculation}</p>}
                <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Responsable :</strong> {selectedArchive.responsable}</p>
                <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Appels :</strong> {selectedArchive.nb_appels}</p>
                <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Créée le :</strong> {new Date(selectedArchive.created_at).toLocaleDateString('fr-FR')}</p>
                <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Archivée le :</strong> {new Date(selectedArchive.date_archivage).toLocaleDateString('fr-FR')}</p>
              </div>

              {selectedArchive.commentaire_initial && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '15px', color: 'var(--text-primary)', borderBottom: '1px solid var(--border-default)', paddingBottom: '8px', marginBottom: '10px' }}>
                    Commentaire initial
                  </h3>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{selectedArchive.commentaire_initial}</p>
                </div>
              )}

              <div>
                <h3 style={{ fontSize: '15px', color: 'var(--text-primary)', borderBottom: '1px solid var(--border-default)', paddingBottom: '8px', marginBottom: '10px' }}>
                  Historique
                </h3>
                {(() => {
                  const comms = selectedArchive.commentaires_json || [];
                  if (comms.length === 0) {
                    return <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '14px' }}>Aucun commentaire</p>;
                  }
                  return (
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {comms.map((c, i) => (
                        <div key={i} style={{
                          background: 'var(--bg-muted)',
                          padding: '12px',
                          marginBottom: '8px',
                          borderRadius: '6px',
                          borderLeft: c.commentaire === 'Demande archivée' ? '3px solid #f59e0b' : '3px solid #0891b2'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '12px' }}>
                            <strong style={{ color: c.commentaire === 'Demande archivée' ? '#f59e0b' : 'light-dark(#0891b2, var(--color-accent-dark))' }}>
                              {c.user_nom} {c.user_prenom}
                            </strong>
                            <span style={{ color: 'var(--text-secondary)' }}>
                              {new Date(c.created_at).toLocaleDateString('fr-FR')} à{' '}
                              {new Date(c.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <div style={{ color: 'var(--text-primary)', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                            {c.commentaire}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="modal-footer">
              {canUnarchive && (
                <button
                  className="btn-secondary"
                  onClick={() => handleUnarchive(selectedArchive.id)}
                  disabled={unarchivingId === selectedArchive.id}
                  title="Restaurer cette demande dans la liste active"
                >
                  {unarchivingId === selectedArchive.id ? '⏳ Désarchivage…' : '🔄 Désarchiver'}
                </button>
              )}
              <button className="btn-secondary" onClick={() => setSelectedArchive(null)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Suivi collaborateurs (super-admin) ──
const SERVICE_LABELS = { VN: 'VN', VO: 'VO', ATELIER: 'Atelier', PIECES: 'Pièces', AUTRE: 'Autres' };
const SERVICE_ICONS  = { VN: '🚘', VO: '🔄', ATELIER: '🔧', PIECES: '📦', AUTRE: '📋' };

function SuiviCollaborateursView({ concessionId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('total_leads');
  const [sortDir, setSortDir] = useState('desc');
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const params = concessionId ? { concession_id: concessionId } : {};
        const res = await demandesApi.getSuiviCollaborateurs(params);
        setData(res.data.collaborateurs || []);
      } catch (err) {
        console.error('Erreur suivi collaborateurs:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [concessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const sortRows = (rows) => [...rows].sort((a, b) => {
    let va, vb;
    if (sortField === 'collaborateur') { va = `${a.responsable_nom} ${a.responsable_prenom || ''}`; vb = `${b.responsable_nom} ${b.responsable_prenom || ''}`; }
    else if (sortField === 'concession') { va = a.concession_nom || ''; vb = b.concession_nom || ''; }
    else if (sortField === 'total_leads') { va = Number(a.total_leads); vb = Number(b.total_leads); }
    else if (sortField === 'traites_aujourdhui') { va = Number(a.traites_aujourdhui); vb = Number(b.traites_aujourdhui); }
    else if (sortField === 'traites_hier') { va = Number(a.traites_hier); vb = Number(b.traites_hier); }
    else if (sortField === 'dernier_traitement') { va = a.dernier_traitement || ''; vb = b.dernier_traitement || ''; }
    else return 0;
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Grouper par service
  const sections = useMemo(() => {
    const order = ['ATELIER', 'VN', 'VO', 'PIECES', 'AUTRE', '_none'];
    const groups = {};
    data.forEach(c => {
      const key = c.service || '_none';
      if (!groups[key]) groups[key] = { key, label: SERVICE_LABELS[key] || 'Non renseigné', icon: SERVICE_ICONS[key] || '❓', entries: [], totalLeads: 0 };
      groups[key].entries.push(c);
      groups[key].totalLeads += Number(c.total_leads);
    });
    return order.filter(k => groups[k]).map(k => groups[k]);
  }, [data]);

  if (loading) return <div className="loading">Chargement...</div>;

  const thBase = { padding: '10px 12px', fontWeight: 600, fontSize: '13px', background: 'var(--bg-muted)', color: 'var(--text-primary)', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' };
  const sortIcon = (field) => sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ⇅';
  const tdStyle = { padding: '8px 12px', fontSize: '13px', borderBottom: '1px solid var(--border-default)' };

  const renderRow = (c) => (
    <tr key={c.responsable_id}>
      <td style={tdStyle}>
        <div style={{ fontWeight: 600 }}>{c.responsable_nom}</div>
        {c.responsable_prenom && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{c.responsable_prenom}</div>}
      </td>
      <td style={{ ...tdStyle, textAlign: 'center' }}>
        <span style={{
          display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontWeight: 700, fontSize: 13,
          background: Number(c.total_leads) > 5 ? 'light-dark(#fee2e2, var(--color-error-light))' : Number(c.total_leads) > 2 ? 'light-dark(#fef9c3, var(--color-warning-light))' : 'light-dark(#e0f2fe, var(--color-info-light))',
          color: Number(c.total_leads) > 5 ? 'light-dark(#b91c1c, var(--color-error-dark))' : Number(c.total_leads) > 2 ? 'light-dark(#854d0e, var(--color-warning-dark))' : 'light-dark(#0369a1, var(--color-info-dark))',
        }}>{c.total_leads}</span>
      </td>
      <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>
        {Number(c.traites_aujourdhui) > 0
          ? <span style={{ color: 'light-dark(var(--color-success), var(--color-success-dark))' }}>{c.traites_aujourdhui}</span>
          : <span style={{ color: 'var(--text-muted)' }}>0</span>}
      </td>
      <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>
        {Number(c.traites_hier) > 0
          ? <span style={{ color: 'light-dark(var(--color-success), var(--color-success-dark))' }}>{c.traites_hier}</span>
          : <span style={{ color: 'var(--text-muted)' }}>0</span>}
      </td>
      <td style={tdStyle}>
        {c.dernier_traitement
          ? new Date(c.dernier_traitement).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
          : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Jamais</span>}
      </td>
    </tr>
  );

  // Synthèse compteurs
  const totalLeads = data.reduce((s, c) => s + Number(c.total_leads), 0);
  const totalAujourdhui = data.reduce((s, c) => s + Number(c.traites_aujourdhui || 0), 0);
  const totalHier = data.reduce((s, c) => s + Number(c.traites_hier), 0);

  return (
    <div>
      {/* Synthèse par service */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {sections.map(s => (
          <div
            key={s.key}
            onClick={() => setCollapsed(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
            style={{
              flex: '1 1 140px', maxWidth: 200, padding: '12px 14px', borderRadius: 10,
              background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
              cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 16 }}>{s.icon}</span>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{s.label}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.entries.length} collab.</span>
              <span style={{ fontWeight: 700, fontSize: 15, color: s.totalLeads > 10 ? 'light-dark(#b91c1c, var(--color-error-dark))' : s.totalLeads > 0 ? 'light-dark(#854d0e, var(--color-warning-dark))' : 'var(--text-muted)' }}>{s.totalLeads} leads</span>
            </div>
          </div>
        ))}
        <div style={{ flex: '1 1 140px', maxWidth: 200, padding: '12px 14px', borderRadius: 10, background: 'var(--bg-muted)', border: '1px solid var(--border-strong)', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>Total</span>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{data.length} collab.</span>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{totalLeads} leads · {totalAujourdhui} auj. · {totalHier} hier</span>
          </div>
        </div>
      </div>

      {/* Sections par service */}
      {sections.map(section => {
        const isCollapsed = collapsed[section.key];
        return (
          <div key={section.key} style={{ marginBottom: 12 }}>
            <div
              onClick={() => setCollapsed(prev => ({ ...prev, [section.key]: !prev[section.key] }))}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
                borderRadius: isCollapsed ? 10 : '10px 10px 0 0', cursor: 'pointer', userSelect: 'none',
              }}
            >
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{isCollapsed ? '▶' : '▼'}</span>
              <span style={{ fontSize: 16 }}>{section.icon}</span>
              <span style={{ fontWeight: 700, fontSize: 14 }}>{section.label}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>({section.entries.length})</span>
              <span style={{ marginLeft: 'auto', fontWeight: 600, fontSize: 13, color: section.totalLeads > 10 ? 'light-dark(#b91c1c, var(--color-error-dark))' : 'var(--text-secondary)' }}>{section.totalLeads} leads</span>
            </div>
            {!isCollapsed && (
              <div className="table-container" style={{ borderRadius: '0 0 10px 10px', borderTop: 'none' }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ ...thBase, textAlign: 'left' }} onClick={() => handleSort('collaborateur')}>Collaborateur{sortIcon('collaborateur')}</th>
                      <th style={{ ...thBase, textAlign: 'center' }} onClick={() => handleSort('total_leads')}>Leads à traiter{sortIcon('total_leads')}</th>
                      <th style={{ ...thBase, textAlign: 'center' }} onClick={() => handleSort('traites_aujourdhui')}>Traités aujourd'hui{sortIcon('traites_aujourdhui')}</th>
                      <th style={{ ...thBase, textAlign: 'center' }} onClick={() => handleSort('traites_hier')}>Traités hier{sortIcon('traites_hier')}</th>
                      <th style={{ ...thBase, textAlign: 'left' }} onClick={() => handleSort('dernier_traitement')}>Dernier traitement{sortIcon('dernier_traitement')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortRows(section.entries).map(renderRow)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {data.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Aucun collaborateur avec des leads en cours</div>
      )}
    </div>
  );
}

// ── Call-center IA — Historique des appels agent vocal ────────────────────────
function CallCenterView({ user }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  // Concession : celle choisie dans le menu (vide = toutes).
  const [filterConcession] = useState(() => (user?.concession_id ? String(user.concession_id) : ''));
  const [filterAction, setFilterAction] = useState('');
  const [transcript, setTranscript] = useState(null);
  const [audioCall, setAudioCall] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const prevAudioUrl = React.useRef(null);

  const ACTION_LABELS = {
    rdv_booked:         { label: 'RDV pris',        cls: 'status-success' },
    callback_requested: { label: 'Rappel demandé',  cls: 'status-info' },
    no_action:          { label: 'Sans action',      cls: 'status-warning' },
    transferred:        { label: 'Transféré',        cls: 'status-info' },
  };

  function formatDuration(secs) {
    if (!secs) return '—';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  const loadCalls = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterConcession) params.concession_id = filterConcession;
      if (filterAction) params.action_taken = filterAction;
      const r = await api.get('/voice-agent/admin/calls', { params });
      setCalls(r.data.calls || []);
      setTotal(r.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterConcession, filterAction]);

  useEffect(() => { loadCalls(); }, [loadCalls]);

  async function openAudio(call) {
    if (prevAudioUrl.current) { URL.revokeObjectURL(prevAudioUrl.current); prevAudioUrl.current = null; }
    setAudioCall(call);
    setAudioUrl(null);
    setAudioError(null);
    setAudioLoading(true);
    try {
      const r = await api.get(`/voice-agent/admin/calls/${call.elevenlabs_conversation_id}/audio`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      prevAudioUrl.current = url;
      setAudioUrl(url);
    } catch (e) {
      const status = e.response?.status;
      setAudioError(status === 404 ? 'Enregistrement audio non disponible pour cet appel.' : 'Impossible de charger l\'audio.');
    } finally {
      setAudioLoading(false);
    }
  }

  function closeAudio() {
    if (prevAudioUrl.current) { URL.revokeObjectURL(prevAudioUrl.current); prevAudioUrl.current = null; }
    setAudioCall(null);
    setAudioUrl(null);
    setAudioError(null);
  }

  // KPIs
  const kpiRdv      = calls.filter(c => c.action_taken === 'rdv_booked').length;
  const kpiRappel   = calls.filter(c => c.action_taken === 'callback_requested').length;
  const kpiSansAct  = calls.filter(c => c.action_taken === 'no_action' || !c.action_taken).length;
  const avgDuration = calls.length
    ? Math.round(calls.reduce((s, c) => s + (c.duration_seconds || 0), 0) / calls.length)
    : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {[
          { label: 'Appels traités', value: total, color: 'var(--color-accent)' },
          { label: 'RDV pris',       value: kpiRdv, color: 'var(--color-success)' },
          { label: 'Rappels',        value: kpiRappel, color: 'var(--color-info)' },
          { label: 'Durée moyenne',  value: formatDuration(avgDuration), color: 'var(--text-secondary)' },
        ].map(k => (
          <div key={k.label} style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
            borderRadius: 10, padding: '16px 20px'
          }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: texteTheme(k.color) }}>{k.value}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="page-toolbar">
        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
        <select
          className={`page-select${filterAction ? ' active-filter' : ''}`}
          value={filterAction}
          onChange={e => setFilterAction(e.target.value)}
        >
          <option value="">Toutes les actions</option>
          <option value="rdv_booked">RDV pris</option>
          <option value="callback_requested">Rappel demandé</option>
          <option value="no_action">Sans action</option>
          <option value="transferred">Transféré</option>
        </select>
        <button className="page-select" onClick={loadCalls} style={{ cursor: 'pointer' }}>
          🔄 Actualiser
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)', alignSelf: 'center' }}>
          {total} appel{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Tableau */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Date & heure</th>
              <th>Concession</th>
              <th>N° appelant</th>
              <th>Durée</th>
              <th>Action réalisée</th>
              <th>RDV / Lead</th>
              <th>Conversation ID</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>Chargement…</td></tr>
            ) : calls.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                Aucun appel enregistré — les appels traités par l'agent vocal apparaîtront ici
              </td></tr>
            ) : calls.map(call => {
              const ai = ACTION_LABELS[call.action_taken] || { label: call.action_taken || '—', cls: '' };
              return (
                <tr key={call.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(call.created_at)}</td>
                  <td>{call.concession_nom || '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{call.caller_number || '—'}</td>
                  <td style={{ textAlign: 'center' }}>{formatDuration(call.duration_seconds)}</td>
                  <td>
                    {call.action_taken
                      ? <span className={`status-badge ${ai.cls}`} style={{ fontSize: 12, padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>{ai.label}</span>
                      : '—'}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {call.rdv_id && <span style={{ color: 'light-dark(var(--color-success), var(--color-success-dark))' }}>RDV #{call.rdv_id}</span>}
                    {call.lead_id && <span style={{ color: 'light-dark(var(--color-info), var(--color-info-dark))' }}>Lead #{call.lead_id}</span>}
                    {!call.rdv_id && !call.lead_id && '—'}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {call.elevenlabs_conversation_id || '—'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {call.transcript && (
                        <button className="btn-icon" title="Voir le transcript" onClick={() => setTranscript(call)}>👁️</button>
                      )}
                      {call.elevenlabs_conversation_id && (
                        <button className="btn-icon" title="Écouter l'appel" onClick={() => openAudio(call)}>🔊</button>
                      )}
                      {!call.transcript && !call.elevenlabs_conversation_id && '—'}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modale transcript */}
      {transcript && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🎙️ Transcript — {formatDate(transcript.created_at)}</h2>
              <button className="modal-close" onClick={() => setTranscript(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: '12px 0', borderBottom: '1px solid var(--border-default)', marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
                <span><strong>Concession :</strong> {transcript.concession_nom || '—'}</span>
                <span><strong>N° appelant :</strong> {transcript.caller_number || '—'}</span>
                <span><strong>Durée :</strong> {formatDuration(transcript.duration_seconds)}</span>
                <span><strong>Action :</strong> {ACTION_LABELS[transcript.action_taken]?.label || transcript.action_taken || '—'}</span>
                {transcript.rdv_id && <span><strong>RDV créé :</strong> #{transcript.rdv_id}</span>}
                {transcript.lead_id && <span><strong>Lead créé :</strong> #{transcript.lead_id}</span>}
                <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{transcript.elevenlabs_conversation_id}</span>
              </div>
              {transcript.elevenlabs_conversation_id && (
                <AudioPlayer conversationId={transcript.elevenlabs_conversation_id} />
              )}
              <pre style={{
                fontFamily: 'monospace', fontSize: 13, lineHeight: 1.7,
                color: 'var(--text-primary)', background: 'var(--bg-muted)',
                border: '1px solid var(--border-default)', borderRadius: 8,
                padding: 16, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0
              }}>{transcript.transcript}</pre>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setTranscript(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {audioCall && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔊 Appel — {formatDate(audioCall.created_at)}</h2>
              <button className="modal-close" onClick={closeAudio}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '20px 24px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20, fontSize: 13, color: 'var(--text-secondary)' }}>
                <span><strong>Concession :</strong> {audioCall.concession_nom || '—'}</span>
                <span><strong>N° appelant :</strong> {audioCall.caller_number || '—'}</span>
                <span><strong>Durée :</strong> {formatDuration(audioCall.duration_seconds)}</span>
              </div>
              {audioLoading && (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 14 }}>
                  Chargement de l'audio…
                </div>
              )}
              {audioError && (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'light-dark(var(--color-error), var(--color-error-dark))', fontSize: 14 }}>
                  {audioError}
                </div>
              )}
              {audioUrl && (
                <audio controls src={audioUrl} style={{ width: '100%' }} autoPlay={false} />
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeAudio}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Lecteur audio autonome (utilisé dans la modale transcript) ────────────────
function AudioPlayer({ conversationId }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const blobRef = React.useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/voice-agent/admin/calls/${conversationId}/audio`, { responseType: 'blob' })
      .then(r => {
        if (cancelled) return;
        const u = URL.createObjectURL(r.data);
        blobRef.current = u;
        setUrl(u);
      })
      .catch(e => {
        if (cancelled) return;
        const status = e.response?.status;
        setError(status === 404 ? 'Enregistrement audio non disponible.' : 'Impossible de charger l\'audio.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (blobRef.current) { URL.revokeObjectURL(blobRef.current); blobRef.current = null; }
    };
  }, [conversationId]);

  if (loading) return (
    <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 0 16px' }}>Chargement de l'audio…</div>
  );
  if (error) return (
    <div style={{ fontSize: 13, color: 'light-dark(var(--color-error), var(--color-error-dark))', padding: '8px 0 16px' }}>{error}</div>
  );
  return (
    <audio controls src={url} style={{ width: '100%', marginBottom: 16 }} />
  );
}

export default LeadsServices;
// Réutilisation cross-module (pattern Takata) : la modale de demande et les
// pictos de sujet sont consommés par le panneau latéral du Planning Atelier.
export { DemandeModal, SUJET_PICTOS };